import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Avatar,
  Backdrop,
  CircularProgress,
  Typography,
  Stack,
  Button,
  Tooltip,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  IconUser,
  IconWorld,
  IconFileInvoice,
  IconTicket,
  IconCalendar,
  IconCalendarStats,
  IconLink,
  IconPlus,
  IconExternalLink,
  IconPrinter,
  IconDatabase,
  IconPhone,
  IconDeviceMobile,
  IconMail,
  IconChecklist,
} from '@tabler/icons-react';
import { PageContainer } from '../components/layout/PageContainer';
import { CARD_SX } from '../styles/designSystem';
import { Chip } from '../components/chip';
import { FormField } from '../components/panel/FormField';
import { LineItemCard } from '../components/tickets/LineItemCard';
import { DateField, SmartSelect, type SmartSelectOption } from '../components/dropdown';
import { GroupedTicketTypePicker, GroupedTicketTypePickerHandle } from '../components/tickets/GroupedTicketTypePicker';
import { AdditionalDetailsRenderer } from '../components/tickets/AdditionalDetailsRenderer';
import { DebugPanel } from '../components/debug/DebugPanel';
import { useDebugSectionFocus } from '../hooks/useDebugSectionFocus';
import { installApiCallLogger } from '../services/apiCallLogger';
import { printRepairTicketLabel, printTicketSheet } from '../services/printApi';
import { LABEL_LANE_COUNTER, LABEL_LANE_WORKSHOP } from '../config/printLanes';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { LinkedTicketDialog, type LinkedTicketContinueMode } from '../components/tickets/LinkedTicketDialog';
import { LinkedTicketChips } from '../components/tickets/LinkedTicketChips';
import { useLinkedTickets } from '../hooks/useLinkedTickets';
import { incrementInvoiceScheduleLine, addScheduleLine, createInvoiceScheduleAndWait } from '../services/invoiceScheduleAdminService';
import { resolveAssignLicenseBilling, countUsernameLines } from '../utils/assignLicenseBilling';
import { getCacheMetadata, invalidateProductsCache, refreshProductsCache } from '../services/productCacheService';
import { getTemplateCacheMetadata, invalidateTicketTypeTemplatesCache } from '../services/templateCacheService';
import { getProfilesConfigCacheMetadata, invalidateProfilesConfigCache, refreshProfilesConfigCache } from '../services/pricingProfilesCacheService';
import type { DebugPanelConfig } from '../types/debugPanel';
import { CustomerLookupControl } from '../components/tickets/Customer/CustomerLookupControl';
import { CustomerHeader } from '../components/tickets/Customer/CustomerHeader';
import { CustomerServicesPanel } from '../components/tickets/Customer/CustomerServicesPanel';
import { CustomerNotesPanel } from '../components/tickets/Customer/CustomerNotesPanel';
import { CustomerAliasesPanel } from '../components/customers/CustomerAliasesPanel';
import { EsetManagePanel } from '../components/eset/EsetManagePanel';
import { useCustomerServices } from '../hooks/useCustomerServices';
import { useCustomerNotes } from '../hooks/useCustomerNotes';
import { useCustomerAliases } from '../hooks/useCustomerAliases';
import { getVendors } from '../services/vendorService';
import type { Vendor } from '@techgitter/contracts';
import type { TicketTypeTemplate, AdditionalDetailsValues, TemplateCustomerContext, LineItem } from '../types/ticketTypeTemplate';
import type { CustomerIndexItemDto, CustomerDesignation, FollowUpTodoResult } from '@techgitter/contracts';
import { deriveSupportProfile, resolvePricingProfile } from '@techgitter/contracts';
import type { ResolvedPricingProfile } from '@techgitter/contracts';
import { syncTemplateLineItems, createDefaultOverrideState, interpolateTemplate, convertRsLineItemsToFormLineItems, FIELD_DEFAULT_RESOLVER_REGISTRY } from '../utils/templateEngine';
import { mapLineItemsToJobLineItems } from '../utils/buildJobPayloadFromTemplate';
import { diffLineItemsForEdit } from '../utils/lineItemEditDiff';
import {
  createRsLineItem,
  updateRsLineItem,
  deleteRsLineItem,
  patchLineItemOverrides,
} from '../services/jobLineItemsSyncService';
import { validateAdditionalDetails, validateFieldRules } from '../components/tickets/AdditionalDetailsRenderer';
import { useTicketTypeTemplates } from '../hooks/useTicketTypeTemplates';
import { filterTemplatesByServices, sortTemplateGroups, isTemplateServiceMismatch, isVisibleStatus } from '../utils/templateFiltering';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import { useProductCatalog, getProductBySku } from '../hooks/useProductCatalog';
import { resolveEolp2Billing } from '../utils/eolp2Billing';
import { findScheduleLineForExactSku, termForSku } from '../utils/licenseScheduleMatch';
import { resolveTemplateForWorkItem } from '../utils/editTemplateMatch';
import { useRenewalSchedule } from '../hooks/useRenewalSchedule';
import { usePricingProfilesConfig } from '../hooks/usePricingProfilesConfig';
import { useAfterHoursSuggestion } from '../hooks/useAfterHoursSuggestion';
import { useJobCreation } from '../hooks/useJobCreation';
import { useJobUpdate } from '../hooks/useJobUpdate';
import { useCustomerHasMonthlyInvoiceSchedule } from '../hooks/useCustomerHasMonthlyInvoiceSchedule';
import { useInvoiceCreation } from '../hooks/useInvoiceCreation';
import { useNavigate } from 'react-router-dom';
import { PastTicketsTable } from '../components/tickets/PastTicketsTable';
import { TaskListPicker } from '../components/tickets/TaskListPicker';
import { ChecklistPanel } from '../components/tickets/ChecklistPanel';
import { TemplateChangeConfirmDialog } from '../components/tickets/TemplateChangeConfirmDialog';
import { TaskListContextPromptDialog, type TaskListContextValues } from '../components/tickets/TaskListContextPromptDialog';
import {
  collectReferencedContextKeys,
  collectReferencedContextKeysFromSections,
  getMissingContextKeys,
  type MissingContextKeys,
} from '../utils/taskListContextRequirements';
import { useTaskLists } from '../hooks/useTaskLists';
import { useTaskDropdowns } from '../hooks/useTaskDropdowns';
import { useChecklist } from '../hooks/useChecklist';
import { getTemplateTaskConfigs } from '../services/templateTaskConfigService';
import { getTaskListsCacheMetadata } from '../services/taskListCacheService';
import type { TemplateTaskConfig, TaskListDefinition } from '@techgitter/contracts';
import { useMsal } from '@azure/msal-react';
import type { JobCreateRequest, JobCreateLineItem, RepairshoprTicketLineItem, LineItemOverride } from '@techgitter/contracts';
import { getCustomerIndexCached, upsertCustomerIndexItem } from '../services/customerIndexCacheService';
import { QuickNewCustomerDialog } from '../components/customers/QuickNewCustomerDialog';
import type { CustomerCreateResult } from '../services/customerCreateApi';
import type { JobUpdatePayload } from '../services/jobCreationService';
import { linkAppointmentToJob } from '../services/appointmentDataService';
import { JOB_STATUSES } from '../components/workitems/utils/workItemStatusConfig';
import { FEATURE_TICKET_LAYOUT_V2 } from '../features';
import { resolveFollowUps, noChargeKey } from '../utils/followUpTodos';
import { FollowUpTodosSection } from '../components/tickets/FollowUpTodosSection';
import './TicketEntryPage.css';

const NO_FOLLOW_UP_RESULTS: FollowUpTodoResult[] = [];

installApiCallLogger();

// Status options for the edit-mode picker (§10 Type B SmartSelect). Bound to
// the Dataverse status value, matching what the work item stores.
const TICKET_STATUS_OPTIONS: SmartSelectOption[] = JOB_STATUSES.map((o) => ({
  value: o.dvValue,
  label: o.label,
}));

// One RepairShopr-shortcut tile — matches the work-item dashboard customer
// panel's `.panel-shortcut` (icon over a 12px label, bordered, hover tint).
const SHORTCUT_TILE_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
  p: '7px 10px',
  border: '0.5px solid #e2eaf3',
  borderRadius: '6px',
  bgcolor: '#fff',
  color: '#555555',
  cursor: 'pointer',
  font: 'inherit',
  '&:hover:not(:disabled)': { bgcolor: '#f0f5fb', borderColor: '#c5d9f0' },
  '&:disabled': { cursor: 'default', opacity: 0.6 },
};

const SHORTCUT_LABEL_SX: SxProps<Theme> = {
  fontSize: 12,
  color: '#646464',
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

// Shared style for the primary save button(s) — accent fill, disabled keeps its
// shape at 0.4 opacity (gotcha §14.11).
const SAVE_BTN_SX: SxProps<Theme> = {
  py: 1.75,
  bgcolor: '#4A90D9',
  fontWeight: 700,
  fontSize: '0.95rem',
  letterSpacing: '0.02em',
  textTransform: 'none',
  '&:hover': { bgcolor: '#3B82C4' },
  '&:focus-visible': { outline: '3px solid #4A90D9', outlineOffset: 2 },
  '&.Mui-disabled': {
    bgcolor: '#4A90D9',
    color: '#ffffff',
    opacity: 0.4,
    cursor: 'not-allowed',
    pointerEvents: 'auto',
  },
};

/** Display-format an AU landline as `02-3456-7890` (handles +61 / spacing). */
const formatAuLandline = (raw?: string): string => {
  const d = (raw || '').replace(/\D/g, '').replace(/^61/, '0');
  return d.length === 10 ? `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}` : (raw || '');
};

/** Display-format an AU mobile as `0412-345-678` (handles +61 / spacing). */
const formatAuMobile = (raw?: string): string => {
  const d = (raw || '').replace(/\D/g, '').replace(/^61/, '0');
  return d.length === 10 ? `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7)}` : (raw || '');
};

interface WorkItemForEdit {
  guid: string;
  id: string;
  number?: number;
  title: string;
  description?: string;
  status?: string;
  problemType?: string;
  customerId?: string;
  rsLineItems?: RepairshoprTicketLineItem[];
  lineItemOverrides?: LineItemOverride[];
  taskList?: Record<string, unknown>;
  taskAudit?: Record<string, unknown>;
  templateFieldValues?: AdditionalDetailsValues;
  jobGUID?: string;
  linkedFromJobGuid?: string;
  linkedFromJobNumber?: number;
  linkedFromJobTitle?: string;
}

/**
 * Hand-off from the Link button to the new-ticket view, via sessionStorage
 * `new_ticket_prefill` (the same key "Convert to job" uses). A prefill carrying
 * `linkedFromJobGuid` is a linked ticket; one without it is an appointment's.
 */
interface LinkedTicketPrefill {
  customerId?: string;
  ticketTypeId: string;
  linkedFromJobGuid: string;
  linkedFromJobNumber?: number;
  linkedFromJobTitle?: string;
  requestingUser?: string;
  templateFieldValues?: AdditionalDetailsValues;
}

interface TicketEntryPageProps {
  onBack?: () => void;
  editWorkItem?: WorkItemForEdit;
  /** Switch to the new-ticket view (the Link button). The prefill is already
   *  in sessionStorage by the time this is called. */
  onCreateLinked?: () => void;
}

/** Display name for a label lane role, for the Print Label tooltip. */
const labelLaneName = (role: string | null | undefined): string => {
  if (!role || role === LABEL_LANE_COUNTER) return 'counter';
  if (role === LABEL_LANE_WORKSHOP) return 'workshop';
  return role;
};

export const TicketEntryPage: React.FC<TicketEntryPageProps> = ({ onBack, editWorkItem, onCreateLinked }) => {
  const isEditMode = !!editWorkItem;
  const editInitializedRef = useRef(false);
  const prefillInitializedRef = useRef(false);
  const sourceAppointmentGuidRef = useRef<string | null>(null);
  // Linked-ticket prefill awaiting the customer's merged template catalog.
  const pendingLinkedPrefillRef = useRef<LinkedTicketPrefill | null>(null);
  // New ticket only: the job this one is being raised from (Link button).
  const [linkedFrom, setLinkedFrom] = useState<{ guid: string; number?: number; title?: string } | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [esetPanelOpen, setEsetPanelOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TicketTypeTemplate | null>(null);
  const [templateVersion, setTemplateVersion] = useState<string | number | undefined>(undefined);
  const [additionalDetails, setAdditionalDetails] = useState<AdditionalDetailsValues>({});
  const [customerContextOverrides, setCustomerContextOverrides] = useState<Record<string, unknown>>({});
  const [suppressedTemplateKeys, setSuppressedTemplateKeys] = useState<Set<string>>(new Set());
  // Edit mode pushes line items to RepairShopr before the job PATCH. That
  // phase runs outside `updatePhase`, so it needs its own in-flight flag and
  // error — a rejected write must leave the form editable to retry from.
  const [isSavingLineItems, setIsSavingLineItems] = useState(false);
  const [lineItemSaveError, setLineItemSaveError] = useState<string | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      id: crypto.randomUUID(),
      source: 'manual',
      lineItemId: '',
      category: '',
      quantity: 0,
      unitCost: 0,
      prefixUserBase: '',
      afterHours: false,
      nonChargeable: false,
      lineDetail: '',
      subtotal: 0,
      finalTotal: 0,
      overrideState: createDefaultOverrideState(),
    },
  ]);
  // Lazy initializer so the `lineItems[0]` read runs once at mount (when the
  // array is seeded with its single default row) — not on every render. A
  // plain object value here is re-evaluated each render, and crashes once
  // lineItems is emptied (e.g. editing a work item with no line items).
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(() =>
    lineItems[0] ? { [lineItems[0].id]: true } : {},
  );
  const servicesPanelRef = useRef<HTMLDivElement | null>(null);
  const ticketTypePickerRef = useRef<GroupedTicketTypePickerHandle>(null);
  const additionalDetailsContainerRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const [focusedServiceId, setFocusedServiceId] = useState<string | null>(null);
  const [serviceFocusRequestKey, setServiceFocusRequestKey] = useState(0);
  const selectedCustomerId = selectedCustomer?.dataverseCustomerId ?? null;
  const selectedCustomerRepairShoprId = Number(selectedCustomer?.repairShoprCustomerId ?? 0);
  const {
    services: customerStatusServices,
    isLoading: isLoadingCustomerStatuses,
    error: customerServicesError,
    canEditServices,
    refresh: refreshCustomerServices,
    create: createCustomerService,
    update: updateCustomerService,
  } = useCustomerServices(selectedCustomerId, 'all');

  const {
    notes: customerNotes,
    isLoading: isLoadingCustomerNotes,
    error: customerNotesError,
    refresh: refreshCustomerNotes,
    update: updateCustomerNotes,
  } = useCustomerNotes(selectedCustomerId);

  const {
    aliases: customerAliases,
    isLoading: isLoadingCustomerAliases,
    error: customerAliasesError,
    refresh: refreshCustomerAliases,
    create: createCustomerAlias,
    update: updateCustomerAlias,
    remove: removeCustomerAlias,
  } = useCustomerAliases(selectedCustomerId);

  const { hasMonthlyInvoiceSchedule } = useCustomerHasMonthlyInvoiceSchedule(
    selectedCustomerId,
  );

  const [vendors, setVendors] = useState<Vendor[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    getVendors(controller.signal).then(setVendors).catch(() => {});
    return () => controller.abort();
  }, []);

  const {
    templates,
    loadedForCustomerId: templatesLoadedForCustomerId,
    isLoading: isLoadingTemplates,
    isStale: isTemplatesStale,
    error: templatesError,
    retry: retryTemplates,
    refresh: refreshTemplates,
  } = useTicketTypeTemplates(selectedCustomerId);

  const { holidays, isLoading: isLoadingHolidays, error: holidaysError } = usePublicHolidays();
  const { products, isLoading: isLoadingProducts, isStale: isProductsStale, error: productsError, retry: retryProducts } = useProductCatalog();
  const {
    config: pricingProfilesConfig,
    isLoading: isLoadingPricingProfiles,
    isStale: isPricingProfilesStale,
    error: pricingProfilesError,
    retry: retryPricingProfiles,
  } = usePricingProfilesConfig();
  const afterHoursSuggestion = useAfterHoursSuggestion(holidays);

  const { schedule: renewalSchedule, schedules: scheduleSummaries, isLoading: isLoadingRenewalSchedule, error: renewalScheduleError } = useRenewalSchedule(selectedCustomerId);

  const { focusedSection, sectionProps, navigateToSection, navigationRequest } = useDebugSectionFocus(['customer', 'ticket', 'cost']);

  const {
    phase: submissionPhase,
    messageId: submissionMessageId,
    error: submissionError,
    rsTicketId,
    rsTicketNumber,
    dvJobId,
    followUpResults = NO_FOLLOW_UP_RESULTS,
    submit: submitJob,
    reset: resetSubmission,
  } = useJobCreation();

  const {
    phase: updatePhase,
    error: updateError,
    submit: submitUpdate,
    reset: resetUpdate,
  } = useJobUpdate();

  const navigate = useNavigate();

  // Save and Take Payment — runs the existing ticket save flow, then
  // creates an invoice for the newly-created ticket, then navigates to
  // the invoice page with the Check Out (payment) dialog pre-opened.
  // Only available for new tickets (not edit mode) where the customer
  // does not have an active Monthly invoice schedule.
  const [saveAndPayActive, setSaveAndPayActive] = useState(false);
  const [saveAndPayError, setSaveAndPayError] = useState<string | null>(null);
  const {
    phase: invoiceCreationPhase,
    invoiceNumber: createdInvoiceNumber,
    error: invoiceCreationError,
    submit: submitInvoiceCreation,
    reset: resetInvoiceCreation,
  } = useInvoiceCreation();

  // Unify create/update phases for shared UI logic
  const activePhase = isEditMode ? updatePhase : submissionPhase;
  const activeError = isEditMode ? updateError : submissionError;
  const activeReset = isEditMode ? resetUpdate : resetSubmission;

  const isSubmitting = activePhase === 'submitting';
  const isAccepted = activePhase === 'accepted';
  const isSucceeded = activePhase === 'succeeded';
  const isOptimisticSuccess = isAccepted || isSucceeded;
  const backgroundFailed = activePhase === 'failed' && (isEditMode || submissionMessageId !== null);
  const formDisabled = isSubmitting || isOptimisticSuccess || backgroundFailed;

  // Task checklist hooks
  const { accounts } = useMsal();
  const userName = accounts[0]?.name ?? accounts[0]?.username ?? 'Unknown';
  const {
    taskListCatalog,
    isLoading: isLoadingTaskLists,
    isStale: isTaskListsStale,
    error: taskListsError,
    refresh: refreshTaskLists,
    invalidate: invalidateTaskLists,
  } = useTaskLists();
  const { dropdownCatalog } = useTaskDropdowns();
  const [templateTaskConfigs, setTemplateTaskConfigs] = useState<TemplateTaskConfig[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    // Pass the selected customer so the API merges that customer's active
    // task-config overrides (replace/append) into the returned catalog —
    // symmetric with useTicketTypeTemplates(selectedCustomerId) above.
    getTemplateTaskConfigs({
      customerId: selectedCustomerId ?? undefined,
      signal: controller.signal,
    })
      .then(catalog => setTemplateTaskConfigs(catalog.configs))
      .catch(() => {});
    return () => controller.abort();
  }, [selectedCustomerId]);

  const [showTemplateChangeConfirm, setShowTemplateChangeConfirm] = useState(false);
  const [pendingTemplateChange, setPendingTemplateChange] = useState<TicketTypeTemplate | null>(null);

  // Pending task list attachment awaiting context input from the user.
  type PendingAttach =
    | { kind: 'list'; def: TaskListDefinition; missing: MissingContextKeys; label: string }
    | { kind: 'config'; config: TemplateTaskConfig; catalog: TaskListDefinition[]; missing: MissingContextKeys; label: string };
  const [pendingAttach, setPendingAttach] = useState<PendingAttach | null>(null);

  const [subjectOverridden, setSubjectOverridden] = useState(false);
  const [descriptionOverridden, setDescriptionOverridden] = useState(false);

  // Both flags are mirrored into refs, and the regeneration effect below reads
  // the refs rather than the state.
  //
  // That effect is declared after the edit-mode hydration effect, so on the
  // commit where hydration runs it runs too — with the *pre-hydration* `false`
  // captured in its closure. It would then "regenerate" the subject it had just
  // been handed, writing '' over the ticket's title (and the description over
  // the ticket's body) in the same commit. A technician sees the pencil open a
  // blank form. Whether it bites depends on whether the effect's other deps
  // happened to change in that commit, which depends on which caches resolved
  // in the same tick — so it reads as intermittent rather than broken.
  //
  // The state copies stay, because they are what makes the effect re-run when a
  // flag changes. The refs are what it trusts.
  const subjectOverriddenRef = useRef(false);
  const descriptionOverriddenRef = useRef(false);
  const markSubjectOverridden = useCallback((value: boolean) => {
    subjectOverriddenRef.current = value;
    setSubjectOverridden(value);
  }, []);
  const markDescriptionOverridden = useCallback((value: boolean) => {
    descriptionOverriddenRef.current = value;
    setDescriptionOverridden(value);
  }, []);

  // The status the edit form opened with — the baseline `hasUnsavedChanges`
  // compares against (the stored value is a slug; the form holds the dvValue).
  const hydratedStatusRef = useRef<string | null>(null);

  // --- Edit mode: hydrate form from existing workitem ---
  useEffect(() => {
    if (!isEditMode || editInitializedRef.current || templates.length === 0) return;
    editInitializedRef.current = true;

    // Clear sessionStorage now that we've consumed it
    sessionStorage.removeItem('edit_workitem_json');

    // 1) Look up the customer from the cached index
    getCustomerIndexCached().then(({ items }) => {
      const customer = items.find(c => c.dataverseCustomerId === editWorkItem.customerId);
      if (customer) setSelectedCustomer(customer);
    });

    // 2) Match template by problemType. A saved job only records the template
    //    *group*, so narrow the family down with the subject and the saved
    //    field values — see resolveTemplateForWorkItem.
    const matchedTemplate = resolveTemplateForWorkItem({
      templates,
      problemType: editWorkItem.problemType,
      title: editWorkItem.title,
      templateFieldValues: editWorkItem.templateFieldValues,
    });
    if (matchedTemplate) {
      setSelectedTemplate(matchedTemplate);
      setTemplateVersion(matchedTemplate.templateVersion);
    }

    // 3) Hydrate form fields
    setTicketSubject(editWorkItem.title || '');
    markSubjectOverridden(true);
    const existingDescription = editWorkItem.description || '';
    const requestedByMatch = existingDescription.match(/^\(Requested by:\s*([^)]*)\)\n?/);
    if (requestedByMatch) {
      setRequestingUser(requestedByMatch[1].trim());
      setShowRequestedByLine(true);
      setTicketDescription(existingDescription.replace(/^\(Requested by:\s*[^)]*\)\n?/, ''));
    } else {
      setTicketDescription(existingDescription);
    }
    markDescriptionOverridden(true);

    const statusSlug = (editWorkItem.status ?? '').toLowerCase();
    const matchedStatus = JOB_STATUSES.find(s => s.value === statusSlug);
    setTicketStatus(matchedStatus?.dvValue ?? 'Resolved');
    hydratedStatusRef.current = matchedStatus?.dvValue ?? 'Resolved';

    // 4) Convert RS line items to form items (with all overrides flagged as user-owned)
    if (editWorkItem.rsLineItems && editWorkItem.rsLineItems.length > 0) {
      const formItems = convertRsLineItemsToFormLineItems(
        editWorkItem.rsLineItems,
        editWorkItem.lineItemOverrides ?? [],
      );
      if (formItems.length > 0) {
        setLineItems(formItems);
        setExpandedItems({ [formItems[0].id]: true });
      }
    }

    // 5) Restore checklist if available (via useChecklist's built-in hydration)
    // The checklist will be restored from the workItem's taskList/taskAudit
    // when we set the template — useChecklist reads from the template config

    // 6) Restore template additional-details values
    if (editWorkItem.templateFieldValues) {
      setAdditionalDetails(editWorkItem.templateFieldValues);
    }
  }, [isEditMode, editWorkItem, templates, markSubjectOverridden, markDescriptionOverridden]);

  const customerContext = useMemo<TemplateCustomerContext>(() => {
    if (!selectedCustomer) return { ...customerContextOverrides };
    const dMap: Record<string, CustomerDesignation> = {
      'Core': 'Core', 'Core+': 'CorePlus', 'CorePlus': 'CorePlus',
    };
    const designation: CustomerDesignation = selectedCustomer.designation
      ? (dMap[selectedCustomer.designation] ?? undefined)
      : undefined;
    const customerType = selectedCustomer.customerType ?? 'Business';
    const supportProfile = deriveSupportProfile({ customerType, designation });

    // Resolve config-driven pricing profile (cascades defaults → designation → catalog)
    let resolvedProfile: ResolvedPricingProfile | undefined;
    if (pricingProfilesConfig && products.length > 0) {
      resolvedProfile = resolvePricingProfile(
        pricingProfilesConfig,
        customerType,
        designation,
        products,
      );
    }

    // Derive active service flags for checklist showWhen conditions
    const activeKeys = new Set(
      customerStatusServices
        .filter(s => isVisibleStatus(s.status))
        .map(s => s.serviceKey.toLowerCase())
    );

    return {
      designation,
      supportProfile,
      resolvedPricingProfile: resolvedProfile,
      repairShoprCustomerId: selectedCustomerRepairShoprId || undefined,
      renewalSchedule,
      city: selectedCustomer.city,
      hasM365: activeKeys.has('m365'),
      hasGoogle: activeKeys.has('google'),
      hasDomains: activeKeys.has('domains'),
      hasAntivirus: activeKeys.has('antivirus'),
      hasBackup: activeKeys.has('backup'),
      hasHosting: activeKeys.has('hosting'),
      ...customerContextOverrides,
    };
  }, [selectedCustomer, selectedCustomerRepairShoprId, renewalSchedule, products, pricingProfilesConfig, customerStatusServices, customerContextOverrides]);

  const {
    checklist,
    auditEntries: checklistAuditEntries,
    hasBlockingTasks,
    progress: checklistProgress,
    attachTaskList,
    applyTemplateConfig,
    detachAll: detachAllTasks,
    setTaskStatus,
    setTaskNote,
    setTaskOption,
    removeTask,
    restoreTask,
    removeSection,
    restoreSection,
    addManualTask,
    getSnapshotForSave,
  } = useChecklist({
    additionalDetails,
    customer: customerContext,
    userName,
    taskDropdowns: dropdownCatalog.dropdowns,
    taskListCatalog: taskListCatalog.taskLists,
  });

  // Intercept task list attachment to prompt for any condition-referenced
  // context that isn't currently in scope. When everything's already
  // present, falls through to attachTaskList directly.
  const handleAttachTaskList = useCallback((def: TaskListDefinition) => {
    const referenced = collectReferencedContextKeys(def, taskListCatalog.taskLists);
    const missing = getMissingContextKeys(referenced, additionalDetails, customerContext);
    if (missing.fieldValueKeys.length === 0 && missing.customerContextKeys.length === 0) {
      attachTaskList(def);
      return;
    }
    setPendingAttach({ kind: 'list', def, missing, label: def.displayName });
  }, [attachTaskList, additionalDetails, customerContext, taskListCatalog.taskLists]);

  const handleContextPromptSubmit = useCallback((values: TaskListContextValues) => {
    if (!pendingAttach) return;
    setAdditionalDetails(prev => ({ ...prev, ...values.fieldValues }));
    setCustomerContextOverrides(prev => ({ ...prev, ...values.customerContextValues }));
    if (pendingAttach.kind === 'list') {
      attachTaskList(pendingAttach.def, {
        additionalDetails: values.fieldValues,
        customer: values.customerContextValues,
      });
    } else {
      applyTemplateConfig(pendingAttach.config, pendingAttach.catalog, {
        additionalDetails: values.fieldValues,
        customer: values.customerContextValues,
      });
    }
    setPendingAttach(null);
  }, [pendingAttach, attachTaskList, applyTemplateConfig]);

  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [ticketDate, setTicketDate] = useState(() =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' })
  );
  // Holds the dvValue (TitleCase) — e.g. 'New', 'Resolved', 'In Progress'.
  // New tickets default to 'New' (a new ticket isn't resolved until the tech
  // marks it so) — switching to 'Resolved' is what enforces task completion.
  const [ticketStatus, setTicketStatus] = useState<string>('New');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketDetail, setTicketDetail] = useState('');
  const [requestingUser, setRequestingUser] = useState('');
  const [showRequestedByLine, setShowRequestedByLine] = useState(false);
  const [printSnack, setPrintSnack] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [scheduleSnack, setScheduleSnack] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  // Existing-subscription license picks captured at submit time. We snapshot
  // these before submitJob runs so the post-success bump uses the values the
  // user actually saved, even if they edit the form again afterwards.
  const pendingScheduleBumpsRef = useRef<Array<{ scheduleId: string; productSku: string; productName: string }>>([]);
  const scheduleBumpsFiredRef = useRef(false);
  // Recurring-schedule mutations for `syncToSchedule` template line items
  // (e.g. EOLP2 email storage) — increment an existing line or create a new one.
  // Snapshotted at submit time, executed once after the ticket save succeeds.
  const pendingScheduleOpsRef = useRef<Array<{
    op: 'increment' | 'add' | 'create';
    scheduleId?: string;
    productSku: string;
    productName: string;
    recurringUnitCost: number;
    /** Units to add — delta for `increment`, line quantity for `add`/`create`. */
    quantity?: number;
    productId?: number;
    taxable?: boolean;
    allowDuplicateSku?: boolean;
    /** For `create`: the new schedule to stand up (with this line on it). */
    createSchedule?: {
      customerDataverseId: string;
      customerRepairShoprId: string;
      name: string;
      frequency: 'Monthly' | 'Yearly';
      nextRun: string;
    };
  }>>([]);

  const printableRsTicketId = rsTicketId ?? editWorkItem?.id ?? null;

  // The user's Settings → default label printer, else sm4rt-api's default
  // (the counter). Only the role travels — see printRepairTicketLabel.
  const currentUser = useCurrentUser();
  const labelRole = currentUser?.preferences.defaultLabelPrinter?.role ?? null;
  const [printingDocument, setPrintingDocument] = useState<'label' | 'sheet' | null>(null);

  // Both go through SM4RT's own print route (POST /api/print → print agent),
  // not RepairShopr's print queue: the label to the user's label lane, the A4
  // sheet to the office laser (the API resolves its lane from the document).
  const handlePrintDocument = useCallback(async (document: 'label' | 'sheet') => {
    if (!printableRsTicketId) return;
    setPrintingDocument(document);
    try {
      if (document === 'label') {
        await printRepairTicketLabel(String(printableRsTicketId), labelRole ? { role: labelRole } : {});
        setPrintSnack({ severity: 'success', message: `Label sent to the ${labelLaneName(labelRole)} printer` });
      } else {
        await printTicketSheet(String(printableRsTicketId));
        setPrintSnack({ severity: 'success', message: 'A4 ticket sent to the office printer' });
      }
    } catch (e) {
      setPrintSnack({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to print',
      });
    } finally {
      setPrintingDocument(null);
    }
  }, [printableRsTicketId, labelRole]);

  const isBusinessCustomer = selectedCustomer?.customerType === 'Business';

  useEffect(() => {
    if (isBusinessCustomer) {
      setShowRequestedByLine(true);
    } else {
      setShowRequestedByLine(false);
      setRequestingUser('');
    }
  }, [isBusinessCustomer]);

  const handleRequestingUserChange = useCallback((value: string) => {
    setShowRequestedByLine(value !== '');
    setRequestingUser(value);
  }, []);

  // Invoice-schedule shortcut state per frequency. Driven by the schedule-level
  // summary (not the flattened line-item view) so paused and itemless schedules
  // are still detected. The button stays usable once a customer is selected: an
  // active schedule opens the schedule editor, a paused one opens it
  // (de-emphasised) for review, and none opens the customer record so a schedule
  // can be set up.
  const buildScheduleState = useCallback((frequency: string) => {
    const matching = scheduleSummaries.filter((s) => s.frequency === frequency);
    const active = matching.filter((s) => !s.paused);
    const source = active.length > 0 ? active : matching;
    const scheduleIds = Array.from(
      new Set(source.map((s) => s.repairShoprScheduleId).filter(Boolean)),
    );
    const kind: 'active' | 'paused' | 'none' =
      matching.length === 0 ? 'none' : active.length > 0 ? 'active' : 'paused';
    return { kind, scheduleIds };
  }, [scheduleSummaries]);

  const monthlySchedule = useMemo(() => buildScheduleState('Monthly'), [buildScheduleState]);
  const annualSchedule = useMemo(() => buildScheduleState('Annually'), [buildScheduleState]);

  const openSchedule = useCallback(
    (state: { kind: 'active' | 'paused' | 'none'; scheduleIds: string[] }) => {
      if (!selectedCustomerRepairShoprId) return;
      if (state.kind === 'none') {
        window.open(
          `https://techsolvers.repairshopr.com/customers/${selectedCustomerRepairShoprId}`,
          '_blank',
          'noopener,noreferrer',
        );
        return;
      }
      state.scheduleIds.forEach((id) =>
        window.open(
          `https://techsolvers.repairshopr.com/schedules/${id}/edit`,
          '_blank',
          'noopener,noreferrer',
        ),
      );
    },
    [selectedCustomerRepairShoprId],
  );

  const composedDescription = useMemo(() => {
    if (!showRequestedByLine) return ticketDescription;
    return `(Requested by: ${requestingUser})\n${ticketDescription}`;
  }, [showRequestedByLine, requestingUser, ticketDescription]);

  const visibleTemplates = useMemo(() => {
    const hasCustomer = Boolean(selectedCustomer);
    const filtered = filterTemplatesByServices(templates, customerStatusServices, hasCustomer);
    const base = showAllTemplates ? templates : filtered;
    const result = sortTemplateGroups(base, customerStatusServices);
    console.log('[visibleTemplates]', {
      showAllTemplates,
      hasCustomer,
      totalTemplates: templates.length,
      filteredCount: filtered.length,
      resultCount: result.length,
      activeKeys: Array.from(new Set(customerStatusServices.filter(s => isVisibleStatus(s.status)).map(s => s.serviceKey))),
      servicesCount: customerStatusServices.length,
    });
    return result;
  }, [templates, customerStatusServices, showAllTemplates, selectedCustomer]);

  const outOfServiceWarning = useMemo(() => {
    if (!selectedTemplate || !selectedCustomer) return false;
    return isTemplateServiceMismatch(selectedTemplate, customerStatusServices);
  }, [selectedTemplate, selectedCustomer, customerStatusServices]);

  // EOLP2 "Increase Email Storage": the schedule-synced template line item and
  // its billing decision (term, month-end buffer, target schedule — or blocked
  // when the customer has no M365 subscription schedule). Shared with the
  // save-time schedule sync so the charge and the recurring mutation agree.
  const scheduleSyncLineItem = useMemo(
    () => selectedTemplate?.templateLineItems?.find((li) => li.syncToSchedule) ?? null,
    [selectedTemplate],
  );

  const eolp2Billing = useMemo(
    () => (scheduleSyncLineItem ? resolveEolp2Billing(renewalSchedule, new Date()) : null),
    [scheduleSyncLineItem, renewalSchedule],
  );

  // Block: no M365 subscription schedule → suppress the schedule-synced line so
  // no EOLP2 charge is added (the tech is warned to set the subscription up first).
  const effectiveSuppressedKeys = useMemo(() => {
    if (eolp2Billing?.blocked && scheduleSyncLineItem) {
      const next = new Set(suppressedTemplateKeys);
      next.add(scheduleSyncLineItem.templateKey);
      return next;
    }
    return suppressedTemplateKeys;
  }, [eolp2Billing, scheduleSyncLineItem, suppressedTemplateKeys]);

  const templateVariables = useMemo<AdditionalDetailsValues>(() => {
    const now = new Date();

    // Resolve a SKU to a friendly product name. Prefer how it reads on the
    // customer's own schedule line (their existing subscription); fall back to
    // the catalog for a license they don't hold yet. Only ever the exact SKU —
    // a same-family sibling is a different product.
    const lookupName = (sku: string | undefined): string => {
      if (!sku) return '';
      const own = findScheduleLineForExactSku(renewalSchedule, sku);
      if (own) return own.productName;
      const product = products.find(p => p.sku === sku);
      return product?.displayName ?? sku;
    };

    // License-change templates use `toLicense` for the new license SKU.
    const licenseSku = (additionalDetails.licenseType || additionalDetails.workspaceEdition || additionalDetails.toLicense) as string | undefined;

    // {renewalProductName} names the license being subscribed to, and always
    // names the *selected* one — their own schedule line when they already
    // hold it, the catalog otherwise ("Microsoft 365 - Business Standard Plan -
    // Monthly Subscription"). Sourcing it from a same-family sibling is what
    // put "Business Premium" on a Business Standard ticket.
    const renewalProductName = lookupName(licenseSku);

    // {renewalMonth} labels the month a monthly subscription starts billing,
    // which is simply the current one. Annual licenses get no month label here
    // — their proration blurb already names the renewal month.
    const renewalMonth = licenseSku && termForSku(licenseSku) === 'monthly'
      ? now.toLocaleString('en-AU', { month: 'long' })
      : '';

    // Templates that only collect `userName` (e.g. license-change) still want
    // {firstName} for line details — derive from the first word.
    const userName = additionalDetails.userName as string | undefined;
    const derivedFirstName = userName ? userName.trim().split(/\s+/)[0] : '';

    // Bulk license assignment (m365_assign_license): a comma-delimited user list
    // from the newline `usernames` field, the resolved license count, and the
    // User/Users noun for the subject.
    const assignUsernames = String((additionalDetails.usernames as string) || '')
      .split('\n').map(s => s.trim()).filter(Boolean);
    const assignCount = Number(additionalDetails.licenseCount) || assignUsernames.length || 1;

    return {
      ...additionalDetails,
      firstName: (additionalDetails.firstName as string) || derivedFirstName,
      toLicenseName: lookupName(additionalDetails.toLicense as string | undefined),
      fromLicenseName: lookupName(additionalDetails.fromLicense as string | undefined),
      licenseProductName: lookupName(additionalDetails.licenseType as string | undefined),
      usernamesList: assignUsernames.join(', '),
      userNoun: assignCount > 1 ? 'Users' : 'User',
      licenseCount: assignCount,
      ticketSubject,
      // Use the composed description so {ticketDescription} in line-item /
      // subject / description templates picks up the "(Requested by: …)" prefix
      ticketDescription: composedDescription,
      month: now.toLocaleString('en-AU', { month: 'long' }),
      year: String(now.getFullYear()),
      renewalMonth,
      renewalProductName,
    };
  }, [additionalDetails, ticketSubject, composedDescription, renewalSchedule, products]);

  useEffect(() => {
    // Read the refs, not the state — see markSubjectOverridden. `subjectOverridden`
    // and `descriptionOverridden` stay in the dep list so a change still schedules
    // this, but within a commit only the refs are current.
    if (!selectedTemplate) {
      if (!subjectOverriddenRef.current) setTicketSubject('');
      if (!descriptionOverriddenRef.current) setTicketDescription('');
      return;
    }
    if (!subjectOverriddenRef.current) {
      const prefix = selectedTemplate.subjectTemplate
        ? interpolateTemplate(selectedTemplate.subjectTemplate, templateVariables)
        : '';
      setTicketSubject(prefix);
    }
    if (selectedTemplate.descriptionTemplate && !descriptionOverriddenRef.current) {
      setTicketDescription(interpolateTemplate(selectedTemplate.descriptionTemplate, templateVariables));
    }
  }, [selectedTemplate, templateVariables, subjectOverridden, descriptionOverridden]);

  useEffect(() => {
    // Wait for pricing profile to resolve before syncing line items.
    // On a cold cache pricingProfilesConfig loads async; without this guard
    // line items would generate with standard catalog rates and may not
    // reprice correctly once the config arrives.
    const pricingReady = !selectedCustomer || customerContext.resolvedPricingProfile;
    if (selectedTemplate && pricingReady) {
      setLineItems(prevItems => {
        // Remove empty default manual line items before syncing so template
        // items replace the placeholder in a single state update.
        const cleaned = prevItems.filter(
          item => item.source !== 'manual' || item.lineItemId !== ''
        );

        const syncedItems = syncTemplateLineItems(
          cleaned,
          selectedTemplate,
          templateVariables,
          effectiveSuppressedKeys,
          customerContext,
          undefined,
          new Date()
        );

        if (syncedItems.length > 0) {
          setExpandedItems(prevExpanded => {
            if (!prevExpanded[syncedItems[0].id]) {
              return { ...prevExpanded, [syncedItems[0].id]: true };
            }
            return prevExpanded;
          });
        }

        return syncedItems;
      });
    }
  }, [selectedTemplate, templateVariables, effectiveSuppressedKeys, customerContext, afterHoursSuggestion, selectedCustomer]);

  const applyTemplateChange = useCallback((template: TicketTypeTemplate | null) => {
    // Wipe checklist on template change
    detachAllTasks();

    setLineItems(prevItems => {
      if (selectedTemplate) {
        // In edit mode, preserve both manual and RS items; in create mode, only manual
        const keepSources: Array<LineItem['source']> = isEditMode ? ['manual', 'rs'] : ['manual'];
        const keptItems = prevItems.filter(item => keepSources.includes(item.source));
        if (keptItems.length > 0) return keptItems;
        const defaultItem: LineItem = {
          id: crypto.randomUUID(),
          source: 'manual',
          lineItemId: '',
          category: '',
          quantity: 0,
          unitCost: 0,
          prefixUserBase: '',
          afterHours: false,
          nonChargeable: false,
          lineDetail: '',
          subtotal: 0,
          finalTotal: 0,
          overrideState: createDefaultOverrideState(),
        };
        setExpandedItems(prev => ({ ...prev, [defaultItem.id]: true }));
        return [defaultItem];
      }
      return prevItems;
    });

    setSuppressedTemplateKeys(new Set());
    setSelectedTemplate(template);
    setTemplateVersion(template?.templateVersion);
    const defaults: AdditionalDetailsValues = {};
    template?.additionalFields?.forEach(f => {
      if (f.defaultResolver) {
        const resolver = FIELD_DEFAULT_RESOLVER_REGISTRY[f.defaultResolver];
        const resolved = resolver?.({ customer: customerContext });
        if (resolved !== undefined && resolved !== '') {
          defaults[f.fieldKey] = resolved;
          return;
        }
      }
      if (f.defaultFromContext) {
        const ctxVal = (customerContext as Record<string, any>)[f.defaultFromContext];
        if (typeof ctxVal === 'string' && ctxVal.trim() !== '') {
          defaults[f.fieldKey] = ctxVal;
          return;
        }
      }
      if (f.defaultValue !== undefined) {
        defaults[f.fieldKey] = f.defaultValue;
      }
    });
    setAdditionalDetails(defaults);
    if (template?.defaultTicketDescription) {
      setTicketDescription(template.defaultTicketDescription);
      markDescriptionOverridden(true);
    } else {
      markDescriptionOverridden(false);
    }
    markSubjectOverridden(false);

    // Apply template task config if one exists
    if (template) {
      const taskConfig = templateTaskConfigs.find(c => c.ticketTypeId === template.ticketTypeId);
      if (taskConfig) {
        // `defaults` was just computed above and not yet flushed into
        // additionalDetails state, so we evaluate "missing" against it
        // directly rather than the stale captured additionalDetails.
        const referenced = collectReferencedContextKeysFromSections(taskConfig.sections, taskListCatalog.taskLists);
        const missing = getMissingContextKeys(referenced, defaults, customerContext);
        if (missing.fieldValueKeys.length === 0 && missing.customerContextKeys.length === 0) {
          applyTemplateConfig(taskConfig, taskListCatalog.taskLists);
        } else {
          setPendingAttach({
            kind: 'config',
            config: taskConfig,
            catalog: taskListCatalog.taskLists,
            missing,
            label: template.displayName,
          });
        }
      }

      setTimeout(() => {
        const firstInput = additionalDetailsContainerRef.current?.querySelector<HTMLInputElement>('input, textarea');
        (firstInput || subjectRef.current)?.focus();
      }, 0);
    }
  }, [selectedTemplate, isEditMode, detachAllTasks, applyTemplateConfig, templateTaskConfigs, customerContext, taskListCatalog, markSubjectOverridden, markDescriptionOverridden]);

  const handleTemplateChange = useCallback((template: TicketTypeTemplate | null) => {
    // If checklist has tasks, show confirmation dialog before changing
    if (checklist && checklist.sections.length > 0) {
      setPendingTemplateChange(template);
      setShowTemplateChangeConfirm(true);
      return;
    }
    applyTemplateChange(template);
  }, [checklist, applyTemplateChange]);

  // --- Pre-fill mode: hydrate form from appointment data ---
  useEffect(() => {
    if (isEditMode || prefillInitializedRef.current || templates.length === 0) return;
    const raw = sessionStorage.getItem('new_ticket_prefill');
    if (!raw) return;
    prefillInitializedRef.current = true;
    sessionStorage.removeItem('new_ticket_prefill');

    try {
      const prefill = JSON.parse(raw) as {
        customerId?: string;
        customerName?: string;
        customerBusinessName?: string;
        title?: string;
        description?: string;
        appointmentType?: string;
        sourceAppointmentGuid?: string;
      } & Partial<LinkedTicketPrefill>;

      // Linked ticket (the Link button on an existing ticket's form). The
      // template waits for the customer's merged catalog — see the effect
      // below — so per-customer overrides apply; only the customer and the
      // internal reference comment are set here.
      if (prefill.linkedFromJobGuid && prefill.ticketTypeId) {
        pendingLinkedPrefillRef.current = prefill as LinkedTicketPrefill;
        setLinkedFrom({
          guid: prefill.linkedFromJobGuid,
          number: prefill.linkedFromJobNumber,
          title: prefill.linkedFromJobTitle,
        });
        if (prefill.customerId) {
          getCustomerIndexCached().then(({ items }) => {
            const customer = items.find(c => c.dataverseCustomerId === prefill.customerId);
            if (customer) setSelectedCustomer(customer);
          });
        }
        // Into Comment (a hidden RS comment), not Detail — Detail is printed
        // on the customer's invoice labour line.
        if (prefill.linkedFromJobNumber) {
          setTicketDetail(
            `Follows on from #${prefill.linkedFromJobNumber}`
            + (prefill.linkedFromJobTitle ? ` — ${prefill.linkedFromJobTitle}` : ''),
          );
        }
        return;
      }

      if (prefill.sourceAppointmentGuid) {
        sourceAppointmentGuidRef.current = prefill.sourceAppointmentGuid;
      }

      // 1) Look up customer
      if (prefill.customerId) {
        getCustomerIndexCached().then(({ items }) => {
          const customer = items.find(c => c.dataverseCustomerId === prefill.customerId);
          if (customer) setSelectedCustomer(customer);
        });
      }

      // 2) Map appointment type → ticket type template
      const appointmentTypeMap: Record<string, string> = {
        'Instore': 'instore_support',
        'Onsite': 'onsite_support',
      };
      const ticketTypeId = appointmentTypeMap[prefill.appointmentType ?? ''] ?? 'remote_support';
      const matchedTemplate = templates.find(t => t.ticketTypeId === ticketTypeId) ?? null;
      if (matchedTemplate) {
        applyTemplateChange(matchedTemplate);
      }

      // 3) Pre-fill subject with appointment title
      if (prefill.title) {
        setTicketSubject(prefill.title);
        markSubjectOverridden(true);
      }
      if (prefill.description) {
        setTicketDescription(prefill.description);
        markDescriptionOverridden(true);
      }
    } catch (err) {
      console.error('[TicketEntryPage] Failed to parse new_ticket_prefill:', err);
    }
  }, [isEditMode, templates, applyTemplateChange, markSubjectOverridden, markDescriptionOverridden]);

  // --- Linked-ticket prefill: apply the template once the catalog is the
  // customer's own. Waiting on `templatesLoadedForCustomerId` (not just a
  // non-empty list) is what makes customer overrides of fields and line items
  // apply — on the render the customer is set, the global catalog is still
  // loaded. The customer can still be changed afterwards; the link stays.
  useEffect(() => {
    const pending = pendingLinkedPrefillRef.current;
    if (!pending || isEditMode) return;
    const customerReady = !pending.customerId || (
      selectedCustomerId === pending.customerId
      && templatesLoadedForCustomerId === pending.customerId
    );
    if (!customerReady || templates.length === 0) return;
    pendingLinkedPrefillRef.current = null;

    const template = templates.find(t => t.ticketTypeId === pending.ticketTypeId) ?? null;
    if (!template) {
      console.warn('[TicketEntryPage] Linked ticket template not found:', pending.ticketTypeId);
      return;
    }
    applyTemplateChange(template);
    // Carry over the source's values for fields this template also has. The
    // functional update runs after applyTemplateChange's defaults, so the
    // source's value wins over a resolver/context/literal default.
    const sourceValues = pending.templateFieldValues ?? {};
    const carried: AdditionalDetailsValues = {};
    template.additionalFields?.forEach(f => {
      const v = sourceValues[f.fieldKey];
      if (v !== undefined && v !== null && v !== '') carried[f.fieldKey] = v;
    });
    if (Object.keys(carried).length > 0) {
      setAdditionalDetails(prev => ({ ...prev, ...carried }));
    }
    if (pending.requestingUser) {
      setRequestingUser(pending.requestingUser);
      setShowRequestedByLine(true);
    }
  }, [isEditMode, templates, templatesLoadedForCustomerId, selectedCustomerId, applyTemplateChange]);

  // Link the source appointment to the newly created job once the orchestrator
  // returns a Dataverse job ID. Fires at most once per submission — the ref is
  // cleared after the link request completes (success or failure) so a retry
  // doesn't double-link.
  const appointmentLinkInFlightRef = useRef(false);
  useEffect(() => {
    const appointmentGuid = sourceAppointmentGuidRef.current;
    if (!appointmentGuid || !dvJobId || appointmentLinkInFlightRef.current) return;
    appointmentLinkInFlightRef.current = true;
    linkAppointmentToJob(appointmentGuid, dvJobId)
      .catch(err => console.error('[TicketEntryPage] Failed to link appointment to job:', err))
      .finally(() => { sourceAppointmentGuidRef.current = null; });
  }, [dvJobId]);

  const handleConfirmTemplateChange = useCallback(() => {
    setShowTemplateChangeConfirm(false);
    applyTemplateChange(pendingTemplateChange);
    setPendingTemplateChange(null);
  }, [pendingTemplateChange, applyTemplateChange]);

  const handleCancelTemplateChange = useCallback(() => {
    setShowTemplateChangeConfirm(false);
    setPendingTemplateChange(null);
  }, []);

  const handleAdditionalDetailsChange = useCallback((values: AdditionalDetailsValues) => {
    setAdditionalDetails(values);
  }, []);

  const handleAddLineItem = useCallback(() => {
    const newItem: LineItem = {
      id: crypto.randomUUID(),
      source: 'manual',
      lineItemId: '',
      category: '',
      quantity: 0,
      unitCost: 0,
      prefixUserBase: '',
      afterHours: false,
      nonChargeable: false,
      lineDetail: '',
      subtotal: 0,
      finalTotal: 0,
      overrideState: createDefaultOverrideState(),
    };
    setLineItems(prevItems => [...prevItems, newItem]);
    setExpandedItems(prevExpanded => ({ ...prevExpanded, [newItem.id]: true }));
  }, []);

  const handleUpdateLineItem = useCallback((id: string, updates: Partial<LineItem>) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id !== id) return item;

      let updatedItem = { ...item, ...updates };

      if (item.source === 'ticketTypeTemplate' && item.overrideState) {
        const overrideState = { ...item.overrideState };

        if ('lineDetail' in updates && updates.lineDetail !== item.lineDetail) {
          overrideState.lineDetail = true;
        }
        if ('quantity' in updates && updates.quantity !== item.quantity) {
          overrideState.quantity = true;
        }
        if ('unitCost' in updates && updates.unitCost !== item.unitCost) {
          overrideState.unitCost = true;
        }
        if ('prefixUserBase' in updates && updates.prefixUserBase !== item.prefixUserBase) {
          overrideState.prefixUserBase = true;
        }
        if ('afterHours' in updates && updates.afterHours !== item.afterHours) {
          overrideState.afterHours = true;
        }
        if ('nonChargeable' in updates && updates.nonChargeable !== item.nonChargeable) {
          overrideState.nonChargeable = true;
        }

        updatedItem = { ...updatedItem, overrideState };
      }

      return updatedItem;
    }));
  }, []);

  const handleDeleteLineItem = useCallback((id: string) => {
    setLineItems(prevItems => {
      const itemToDelete = prevItems.find(item => item.id === id);

      if (itemToDelete?.source === 'ticketTypeTemplate' && itemToDelete.templateKey) {
        setSuppressedTemplateKeys(prev => new Set(prev).add(itemToDelete.templateKey!));
      }

      return prevItems.filter(item => item.id !== id);
    });

    setExpandedItems(prevExpanded => {
      const newExpandedItems = { ...prevExpanded };
      delete newExpandedItems[id];
      return newExpandedItems;
    });
  }, []);

  const handleExpandChange = useCallback((id: string, expanded: boolean) => {
    setExpandedItems(prevExpanded => ({ ...prevExpanded, [id]: expanded }));
  }, []);

  useEffect(() => {
    setFocusedServiceId(null);
    setServiceFocusRequestKey(0);
  }, [selectedCustomerId]);


  const handleCustomerCreated = useCallback((result: CustomerCreateResult) => {
    setNewCustomerOpen(false);
    if (!result.customer) return;
    const c = result.customer;
    const displayName = c.businessName || c.fullName || `${c.firstName} ${c.lastName}`.trim();
    const customerType: CustomerIndexItemDto['customerType'] = c.businessName ? 'Business' : 'Residential';
    const indexItem: CustomerIndexItemDto = {
      dataverseCustomerId: result.dataverseCustomerId,
      repairShoprCustomerId: String(result.repairShoprCustomerId),
      displayName,
      customerType,
      fullName: c.fullName,
      businessName: c.businessName,
      designation: c.designation,
      email: c.email,
      phone: c.phone,
      mobile: c.mobile,
      domains: c.domains,
      alias: c.alias,
    };
    upsertCustomerIndexItem(indexItem);
    setSelectedCustomer(indexItem);
  }, []);

  const selectedTicketType = selectedTemplate;

  // Follow-up to-dos the template schedules (create only — a follow-up is
  // created with the ticket, never on an edit). Each raises a ticket of its own
  // on its date; the tech's per-line No charge choice is kept here.
  const [followUpNoCharge, setFollowUpNoCharge] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setFollowUpNoCharge({});
  }, [selectedTemplate?.ticketTypeId]);
  const followUpResolution = useMemo(() => {
    if (isEditMode || !selectedTemplate?.followUpTodos?.length || !selectedCustomer) {
      return { followUps: [], problems: [] };
    }
    return resolveFollowUps({
      template: selectedTemplate,
      templates,
      customer: {
        dataverseId: selectedCustomer.dataverseCustomerId,
        repairShoprId: selectedCustomer.repairShoprCustomerId,
        customerType: selectedCustomer.customerType,
        designation: selectedCustomer.designation,
      },
      additionalDetails,
      products,
      pricingProfilesConfig,
      taskConfigs: templateTaskConfigs,
      taskListCatalog: taskListCatalog.taskLists,
      taskDropdowns: dropdownCatalog.dropdowns,
      noChargeOverrides: followUpNoCharge,
    });
  }, [isEditMode, selectedTemplate, selectedCustomer, templates, additionalDetails, products, pricingProfilesConfig, templateTaskConfigs, taskListCatalog.taskLists, dropdownCatalog.dropdowns, followUpNoCharge]);
  const handleFollowUpNoCharge = useCallback((followUpIndex: number, lineIndex: number, noCharge: boolean) => {
    setFollowUpNoCharge(prev => ({ ...prev, [noChargeKey(followUpIndex, lineIndex)]: noCharge }));
  }, []);

  // Rules between fields (End Date before Start Date …) shown on the field
  // itself; strictErrors below still carries them, so they block Save too.
  const fieldRuleErrors = useMemo(
    () => validateFieldRules(selectedTemplate?.additionalFields ?? [], additionalDetails, customerContext),
    [selectedTemplate, additionalDetails, customerContext],
  );

  const strictErrors = useMemo(() => {
    const errors: string[] = [];
    // A follow-up that can't be built must stop the save: leaving it out
    // would raise this ticket and silently never schedule what undoes it.
    errors.push(...followUpResolution.problems.map(p => p.message));
    // Only on create: the requesting user is carried in the "(Requested by: …)"
    // description prefix, and the update PATCH never sends a description — so on
    // an existing ticket this would block the save over a field that goes nowhere.
    if (!isEditMode && selectedTemplate && isBusinessCustomer && !requestingUser.trim()) {
      errors.push('Requesting user is required');
    }
    // In edit mode this holds with or without a template: the update PATCHes
    // `subject` onto a ticket that already has one, so an empty box would
    // overwrite the real subject with ''. Relaxing the ticket-type gate for
    // edits (a ticket raised in RepairShopr matches no template) is what made
    // that reachable, so the subject has to carry the check itself.
    if ((selectedTemplate || isEditMode) && !ticketSubject.trim()) {
      errors.push('Subject is required');
    }
    if (selectedTemplate?.additionalFields) {
      const fieldErrors = validateAdditionalDetails(selectedTemplate.additionalFields, additionalDetails, customerContext);
      errors.push(...Object.values(fieldErrors));
    }
    return errors;
  }, [isEditMode, ticketSubject, selectedTemplate, additionalDetails, customerContext, isBusinessCustomer, requestingUser, followUpResolution.problems]);

  const { canSave, saveDisabledReason, baseCanSave } = useMemo(() => {
    if (isSavingLineItems) return { canSave: false, baseCanSave: false, saveDisabledReason: 'Saving line items...' };
    if (isSubmitting) return { canSave: false, baseCanSave: false, saveDisabledReason: 'Ticket is being submitted...' };
    if (isOptimisticSuccess) return { canSave: false, baseCanSave: false, saveDisabledReason: 'Ticket already submitted' };
    if (backgroundFailed) return { canSave: false, baseCanSave: false, saveDisabledReason: 'Ticket creation failed' };

    // Edit mode PATCHes an existing ticket: the customer isn't editable here and
    // jobType is optional on the update, so neither gates the save. A ticket
    // raised in RepairShopr usually has a problemType that matches no template
    // group, which would otherwise leave Update Ticket permanently disabled with
    // no way out — the customer field is read-only and picking a ticket type
    // just to enable the button rewrites the subject, description and lines.
    const missingCustomer = !isEditMode && !selectedCustomer;
    const missingTicketType = !isEditMode && !selectedTicketType;
    const hasStrictErrors = strictErrors.length > 0;
    // Task blocking only applies when resolving
    const blockedByTasks = ticketStatus.toLowerCase() === 'resolved' && hasBlockingTasks;
    // baseCanSave excludes the resolve-blocking so the new-ticket "Save as
    // Resolved" button can gate on tasks while "Save as New" stays free.
    const nextBaseCanSave = !missingCustomer && !missingTicketType && !hasStrictErrors;
    const nextCanSave = nextBaseCanSave && !blockedByTasks;

    let reason = '';
    if (missingCustomer && missingTicketType) {
      reason = 'Select a customer and ticket type';
    } else if (missingCustomer) {
      reason = 'Select a customer';
    } else if (missingTicketType) {
      reason = 'Select a ticket type';
    } else if (hasStrictErrors) {
      reason = strictErrors.join(', ');
    } else if (blockedByTasks) {
      reason = 'All tasks must be actioned before resolving';
    }

    return {
      canSave: nextCanSave,
      baseCanSave: nextBaseCanSave,
      saveDisabledReason: reason,
    };
  }, [isEditMode, selectedCustomer, selectedTicketType, strictErrors, isSavingLineItems, isSubmitting, isOptimisticSuccess, backgroundFailed, ticketStatus, hasBlockingTasks]);

  const ticketTotal = lineItems.reduce((sum, item) => sum + (isFinite(item.finalTotal) ? item.finalTotal : 0), 0);

  const buildJobPayload = useCallback((statusOverride?: string): JobCreateRequest => {
    if (!selectedCustomer || !selectedTemplate) {
      throw new Error('Customer and template are required');
    }

    const jobLineItems: JobCreateLineItem[] = mapLineItemsToJobLineItems(lineItems, products);

    const request: JobCreateRequest = {
      customer: {
        dataverseId: selectedCustomer.dataverseCustomerId,
        repairShoprId: selectedCustomer.repairShoprCustomerId,
      },
      subject: ticketSubject,
      description: composedDescription,
      jobType: selectedTemplate.group,
      ticketDate,
      // New tickets are constrained to 'new' | 'resolved' by the contract
      status: (statusOverride ?? ticketStatus).toLowerCase() === 'new' ? 'new' : 'resolved',
      lineItems: jobLineItems,
    };

    if (ticketDetail.trim()) {
      request.detail = ticketDetail.trim();
    }

    // Written once, server-side, as cr001_linked_from_job_id — never put it in
    // dvOverlay (the API refuses that).
    if (linkedFrom) {
      request.linkedFromJobGuid = linkedFrom.guid;
    }

    // Created by sm4rt-api once the ticket is accepted; each raises its own
    // ticket on its date.
    if (followUpResolution.followUps.length > 0) {
      request.followUpTodos = followUpResolution.followUps.map(f => f.request);
    }

    // Include task checklist + audit, template field values and the ticket
    // description in dv_overlay. `description` above only reaches RepairShopr
    // (interpolated into the line-item details) — cr001_description is what
    // lets the edit screen read the text back out. Store the *composed* form so
    // the "(Requested by: …)" prefix round-trips the way hydration expects it.
    const checklistSnapshot = getSnapshotForSave();
    request.dvOverlay = {
      ...(checklistSnapshot ? {
        cr001_task_list: JSON.stringify(checklistSnapshot.checklist),
        cr001_task_audit: JSON.stringify(checklistSnapshot.audit),
      } : {}),
      cr001_template_field_values: JSON.stringify(additionalDetails),
      cr001_description: composedDescription,
    };

    return request;
  }, [selectedCustomer, selectedTemplate, lineItems, ticketSubject, composedDescription, ticketDetail, ticketDate, ticketStatus, products, getSnapshotForSave, additionalDetails, linkedFrom, followUpResolution.followUps]);

  const buildUpdatePayload = useCallback((): JobUpdatePayload => {
    const payload: JobUpdatePayload = {};

    if (editWorkItem) {
      payload.rsTicketId = editWorkItem.id;
    }

    // The PATCH is partial, so an omitted key leaves the stored value alone.
    // Send the subject only when there is one — `canSave` already blocks an
    // empty box, and this is the backstop that keeps a blank form from
    // erasing a real ticket's subject if it ever gets past that.
    if (ticketSubject.trim()) {
      payload.subject = ticketSubject;
    }
    // jobType only when a template is resolved; an RS-originated ticket may
    // match none, and sending a guess would relabel it.
    if (selectedTemplate) {
      payload.jobType = selectedTemplate.group;
    }

    // Include status (already in dvValue / TitleCase form)
    payload.status = ticketStatus;

    // Include checklist, template field values and the edited description in
    // dv_overlay — the same channel the create path uses.
    const checklistSnapshot = getSnapshotForSave();
    payload.dvOverlay = {
      ...(checklistSnapshot ? {
        cr001_task_list: JSON.stringify(checklistSnapshot.checklist),
        cr001_task_audit: JSON.stringify(checklistSnapshot.audit),
      } : {}),
      cr001_template_field_values: JSON.stringify(additionalDetails),
      cr001_description: composedDescription,
    };

    return payload;
  }, [selectedTemplate, editWorkItem, ticketSubject, ticketStatus, composedDescription, getSnapshotForSave, additionalDetails]);

  const handleSaveTicket = useCallback(async (statusOverride?: string) => {
    try {
      if (isEditMode && editWorkItem) {
        // Line items go first and must be accepted before the job PATCH.
        // The PATCH carries no line items of its own, so letting it land after
        // a rejected line-item write would lock the form on an optimistic
        // success while the charges the user just added were quietly dropped
        // — the failure this whole path exists to stop.
        const diff = diffLineItemsForEdit(
          lineItems,
          editWorkItem.rsLineItems ?? [],
          {
            deleteMissing: true,
            previousOverrides: editWorkItem.lineItemOverrides ?? [],
          },
        );

        if (!diff.isEmpty) {
          setIsSavingLineItems(true);
          setLineItemSaveError(null);
          try {
            // Deletes first: a line replaced by an edited copy of itself would
            // otherwise briefly exist twice on the ticket.
            for (const rsLineItemId of diff.deletes) {
              await deleteRsLineItem(editWorkItem.guid, rsLineItemId);
            }
            await Promise.all([
              ...diff.updates.map(({ rsLineItemId, fields }) =>
                updateRsLineItem(editWorkItem.guid, rsLineItemId, fields),
              ),
              ...diff.creates.map(item =>
                createRsLineItem(editWorkItem.guid, item),
              ),
            ]);
            if (diff.overrides.length > 0) {
              await patchLineItemOverrides(editWorkItem.guid, diff.overrides);
            }
          } catch (err) {
            // Stop short of the job PATCH and leave the form editable so the
            // user can retry. Retrying is safe: accepted writes are already
            // key-backed, so the rebuilt diff won't re-send them.
            setLineItemSaveError(
              err instanceof Error ? err.message : 'Failed to save line items',
            );
            return;
          } finally {
            setIsSavingLineItems(false);
          }
        }

        const payload = buildUpdatePayload();
        await submitUpdate(editWorkItem.guid, payload);
      } else {
        // A template that drives the recurring schedule via a `syncToSchedule`
        // line item owns the schedule mutation itself (below); the generic
        // license-picker bump must NOT also fire, or the line double-counts.
        const templateSyncsSchedule = selectedTemplate?.templateLineItems?.some(
          (li) => li.syncToSchedule,
        ) ?? false;

        // Snapshot existing-subscription license selections so we can bump the
        // matching scheduled invoice line after the ticket save succeeds.
        const bumps: Array<{ scheduleId: string; productSku: string; productName: string }> = [];
        if (!templateSyncsSchedule && selectedTemplate?.additionalFields && renewalSchedule.length > 0) {
          for (const field of selectedTemplate.additionalFields) {
            if (field.type !== 'license-picker') continue;
            const selectedSku = additionalDetails[field.fieldKey];
            if (typeof selectedSku !== 'string' || !selectedSku) continue;
            const match = renewalSchedule.find(item => item.productSku === selectedSku);
            if (match && match.scheduleId) {
              bumps.push({
                scheduleId: match.scheduleId,
                productSku: match.productSku,
                productName: match.productName || match.productSku,
              });
            }
          }
        }
        pendingScheduleBumpsRef.current = bumps;
        scheduleBumpsFiredRef.current = false;

        // Schedule-synced template line items: increment the customer's matching
        // recurring line, add it, or create the schedule. The billing decision
        // is owned by the same resolver that priced the ticket line, so charge
        // and recurring mutation stay in lock-step. Two strategies:
        //   - `assignLicense` (m365_assign_license): resolveAssignLicenseBilling,
        //     quantity = license count, may create an annual schedule.
        //   - default (EOLP2 email storage): resolveEolp2Billing, quantity 1.
        const scheduleOps: typeof pendingScheduleOpsRef.current = [];
        if (scheduleSyncLineItem && selectedCustomer) {
          if (scheduleSyncLineItem.pricingRule === 'assignLicense') {
            const sku = additionalDetails.licenseType;
            const count =
              Number(additionalDetails.licenseCount) ||
              countUsernameLines(additionalDetails.usernames) ||
              1;
            if (typeof sku === 'string' && sku) {
              const billing = resolveAssignLicenseBilling(renewalSchedule, new Date(), sku, count);
              if (billing) {
                const product = getProductBySku(billing.sku);
                const common = {
                  productSku: billing.sku,
                  productName: product?.displayName ?? billing.sku,
                  recurringUnitCost: billing.recurringUnitCost,
                  quantity: billing.count,
                  productId: product?.productId ? Number(product.productId) : undefined,
                  taxable: product?.taxable,
                };
                if (billing.schedule.action === 'create') {
                  scheduleOps.push({
                    ...common,
                    op: 'create',
                    createSchedule: {
                      customerDataverseId: selectedCustomer.dataverseCustomerId,
                      customerRepairShoprId: selectedCustomer.repairShoprCustomerId,
                      name: `${selectedCustomer.name ?? 'Customer'} — M365 (${billing.schedule.frequency})`,
                      frequency: billing.schedule.frequency,
                      nextRun: billing.schedule.nextRun,
                    },
                  });
                } else {
                  scheduleOps.push({
                    ...common,
                    op: billing.schedule.action,
                    scheduleId: billing.schedule.scheduleId,
                  });
                }
              }
            }
          } else if (
            eolp2Billing &&
            !eolp2Billing.blocked &&
            eolp2Billing.sku &&
            eolp2Billing.targetScheduleId
          ) {
            const product = getProductBySku(eolp2Billing.sku);
            const wantsCreate = scheduleSyncLineItem.createIfMissingSchedule ?? false;
            if (eolp2Billing.existingLine || wantsCreate) {
              scheduleOps.push({
                op: eolp2Billing.existingLine ? 'increment' : 'add',
                scheduleId: eolp2Billing.targetScheduleId,
                productSku: eolp2Billing.sku,
                productName:
                  eolp2Billing.existingLine?.productName ?? product?.displayName ?? eolp2Billing.sku,
                recurringUnitCost: eolp2Billing.recurringUnitCost ?? product?.unitCost ?? 0,
                quantity: 1,
                productId: product?.productId ? Number(product.productId) : undefined,
                taxable: product?.taxable,
              });
            }
          }
        }
        pendingScheduleOpsRef.current = scheduleOps;

        const payload = buildJobPayload(statusOverride);
        await submitJob(payload);
      }
    } catch (err) {
      console.error('Failed to build job payload:', err);
    }
  }, [isEditMode, editWorkItem, lineItems, buildJobPayload, buildUpdatePayload, submitJob, submitUpdate, selectedTemplate, selectedCustomer, renewalSchedule, additionalDetails, scheduleSyncLineItem, eolp2Billing]);

  // --- Unsaved changes on an existing ticket (the Link button's guard) ---
  // Compared against what is stored, not a snapshot taken at hydration: the
  // form settles over several commits (customer lookup, template effects,
  // line-item sync), so a snapshot would either fire early or not at all.
  // Line items reuse the save path's own diff, so "dirty" means exactly
  // "Update Ticket would write something".
  const hasUnsavedChanges = useMemo(() => {
    if (!isEditMode || !editWorkItem || !editInitializedRef.current) return false;
    if (isOptimisticSuccess) return false;
    if (ticketSubject !== (editWorkItem.title || '')) return true;
    const normaliseDesc = (d: string) => d.replace(/^(\(Requested by:[^)]*\))\n?/, '$1\n').trimEnd();
    if (normaliseDesc(composedDescription) !== normaliseDesc(editWorkItem.description || '')) return true;
    if (hydratedStatusRef.current !== null && ticketStatus !== hydratedStatusRef.current) return true;
    const sortedJson = (v: Record<string, unknown> | undefined) =>
      JSON.stringify(Object.keys(v ?? {}).sort().map(k => [k, (v ?? {})[k]]));
    if (sortedJson(additionalDetails) !== sortedJson(editWorkItem.templateFieldValues)) return true;
    if (checklistAuditEntries.length > 0) return true;
    const diff = diffLineItemsForEdit(lineItems, editWorkItem.rsLineItems ?? [], {
      deleteMissing: true,
      previousOverrides: editWorkItem.lineItemOverrides ?? [],
    });
    return !diff.isEmpty;
  }, [isEditMode, editWorkItem, isOptimisticSuccess, ticketSubject, composedDescription, ticketStatus, additionalDetails, checklistAuditEntries, lineItems]);

  // --- Link: raise a new ticket from a template, linked to this one ---
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  // Set by "Save & continue": the hand-off waits for the update to be accepted.
  const [pendingLinkTemplate, setPendingLinkTemplate] = useState<TicketTypeTemplate | null>(null);
  const sourceJobGuid = editWorkItem ? (editWorkItem.jobGUID ?? editWorkItem.guid) : null;
  const { children: linkedChildren, isLoading: isLoadingLinkedChildren } = useLinkedTickets(
    isEditMode ? sourceJobGuid : null,
  );

  const goToLinkedTicket = useCallback((template: TicketTypeTemplate) => {
    if (!editWorkItem || !sourceJobGuid) return;
    const prefill: LinkedTicketPrefill = {
      customerId: editWorkItem.customerId,
      ticketTypeId: template.ticketTypeId,
      linkedFromJobGuid: sourceJobGuid,
      linkedFromJobNumber: editWorkItem.number,
      linkedFromJobTitle: ticketSubject || editWorkItem.title,
      requestingUser: requestingUser.trim() || undefined,
      templateFieldValues: additionalDetails,
    };
    sessionStorage.setItem('new_ticket_prefill', JSON.stringify(prefill));
    setLinkDialogOpen(false);
    setPendingLinkTemplate(null);
    onCreateLinked?.();
  }, [editWorkItem, sourceJobGuid, ticketSubject, requestingUser, additionalDetails, onCreateLinked]);

  const handleLinkContinue = useCallback((template: TicketTypeTemplate, mode: LinkedTicketContinueMode) => {
    if (mode === 'save') {
      setLineItemSaveError(null);
      setPendingLinkTemplate(template);
      void handleSaveTicket();
      return;
    }
    goToLinkedTicket(template);
  }, [goToLinkedTicket, handleSaveTicket]);

  // Finish a "Save & continue" once the update is accepted. A rejected
  // line-item write or a failed update abandons the hand-off and closes the
  // dialog, so the form's own error is what the user sees.
  useEffect(() => {
    if (!pendingLinkTemplate) return;
    if (updatePhase === 'accepted' || updatePhase === 'succeeded') {
      goToLinkedTicket(pendingLinkTemplate);
    } else if (updatePhase === 'failed' || lineItemSaveError) {
      setPendingLinkTemplate(null);
      setLinkDialogOpen(false);
    }
  }, [pendingLinkTemplate, updatePhase, lineItemSaveError, goToLinkedTicket]);

  // After a successful job create, bump the quantity on any existing
  // subscription the user picked via a license-picker. Fires once per save.
  useEffect(() => {
    if (isEditMode) return;
    if (submissionPhase !== 'succeeded') return;
    if (scheduleBumpsFiredRef.current) return;
    const bumps = pendingScheduleBumpsRef.current;
    const ops = pendingScheduleOpsRef.current;
    if (bumps.length === 0 && ops.length === 0) return;

    scheduleBumpsFiredRef.current = true;
    pendingScheduleBumpsRef.current = [];
    pendingScheduleOpsRef.current = [];

    (async () => {
      for (const bump of bumps) {
        try {
          const result = await incrementInvoiceScheduleLine(bump.scheduleId, {
            productSku: bump.productSku,
            delta: 1,
          });
          setScheduleSnack({
            severity: 'success',
            message: `Updated scheduled invoice from ${result.previousQuantity} to ${result.newQuantity} units of ${result.productName}`,
          });
        } catch (err) {
          console.error('Failed to increment scheduled invoice line:', err);
          setScheduleSnack({
            severity: 'error',
            message: `Failed to update scheduled invoice for ${bump.productName}: ${err instanceof Error ? err.message : 'unknown error'}`,
          });
        }
      }

      // Schedule-synced template line items: increment an existing recurring
      // line, add a new one, or create the schedule outright.
      for (const op of ops) {
        try {
          const units = op.quantity ?? 1;
          if (op.op === 'increment' && op.scheduleId) {
            const result = await incrementInvoiceScheduleLine(op.scheduleId, {
              productSku: op.productSku,
              delta: units,
            });
            setScheduleSnack({
              severity: 'success',
              message: `Updated scheduled invoice from ${result.previousQuantity} to ${result.newQuantity} units of ${result.productName}`,
            });
          } else if (op.op === 'create' && op.createSchedule) {
            await createInvoiceScheduleAndWait({
              name: op.createSchedule.name,
              frequency: op.createSchedule.frequency,
              nextRun: op.createSchedule.nextRun,
              customer: {
                dataverseId: op.createSchedule.customerDataverseId,
                repairShoprId: op.createSchedule.customerRepairShoprId ?? '',
              },
              lineItems: [{
                name: op.productSku,
                quantity: units,
                priceRetail: op.recurringUnitCost,
                productId: op.productId,
                taxable: op.taxable,
                oneTimeCharge: false,
              }],
            });
            setScheduleSnack({
              severity: 'success',
              message: `Created a recurring invoice schedule with ${op.productName} (applies next cycle)`,
            });
          } else if (op.scheduleId) {
            await addScheduleLine(op.scheduleId, {
              name: op.productSku,
              quantity: units,
              priceRetail: op.recurringUnitCost,
              productId: op.productId,
              taxable: op.taxable,
              oneTimeCharge: false,
              allowDuplicateSku: op.allowDuplicateSku,
            });
            setScheduleSnack({
              severity: 'success',
              message: `Added ${op.productName} to the recurring invoice schedule (applies next cycle)`,
            });
          }
        } catch (err) {
          console.error('Failed to sync recurring schedule line:', err);
          setScheduleSnack({
            severity: 'error',
            message: `Failed to update scheduled invoice for ${op.productName}: ${err instanceof Error ? err.message : 'unknown error'}`,
          });
        }
      }
    })();
  }, [submissionPhase, isEditMode]);

  const handleSaveAndTakePayment = useCallback(async (statusOverride?: string) => {
    if (saveAndPayActive) return;
    setSaveAndPayError(null);
    resetInvoiceCreation();
    setSaveAndPayActive(true);
    await handleSaveTicket(statusOverride);
  }, [saveAndPayActive, resetInvoiceCreation, handleSaveTicket]);

  // New-ticket save buttons: pick the status, then save. Resolving with no
  // monthly invoice schedule routes through the save-and-take-payment flow.
  const handleSaveAs = useCallback((status: 'New' | 'Resolved') => {
    setTicketStatus(status);
    if (status === 'Resolved' && hasMonthlyInvoiceSchedule === false) {
      handleSaveAndTakePayment(status);
    } else {
      handleSaveTicket(status);
    }
  }, [hasMonthlyInvoiceSchedule, handleSaveAndTakePayment, handleSaveTicket]);

  // Once the ticket creation succeeds (rsTicketId + dvJobId known), kick
  // off invoice creation. Guarded by saveAndPayActive so the plain "Save
  // Ticket" path continues to behave as before.
  useEffect(() => {
    if (!saveAndPayActive) return;
    if (submissionPhase !== 'succeeded') return;
    if (invoiceCreationPhase !== 'idle') return;
    if (!rsTicketId || !dvJobId) return;
    if (!selectedCustomerRepairShoprId) {
      setSaveAndPayActive(false);
      setSaveAndPayError('Customer is not linked to RepairShopr — cannot create invoice');
      return;
    }
    void submitInvoiceCreation({
      rsCustomerId: selectedCustomerRepairShoprId,
      rsTicketId: Number(rsTicketId) || 0,
      dvJobGuid: dvJobId,
    });
  }, [
    saveAndPayActive,
    submissionPhase,
    invoiceCreationPhase,
    rsTicketId,
    dvJobId,
    selectedCustomerRepairShoprId,
    submitInvoiceCreation,
  ]);

  // Save and Take Payment: surface ticket creation failure and bail out.
  useEffect(() => {
    if (!saveAndPayActive) return;
    if (submissionPhase === 'failed') {
      setSaveAndPayActive(false);
      setSaveAndPayError(submissionError ?? 'Failed to save ticket');
    }
  }, [saveAndPayActive, submissionPhase, submissionError]);

  // On invoice success, navigate to the invoice page and auto-open the
  // Check Out dialog. RS auto-copies the ticket's line items into the
  // new invoice and the orchestrator syncs them to cr001_line_items; do
  // not pass preselectedLineItems here, because re-adding them on the
  // InvoiceDetailPage clobbers the DV column via _sync_dv_line_items.
  useEffect(() => {
    if (!saveAndPayActive) return;
    if (invoiceCreationPhase === 'succeeded' && createdInvoiceNumber) {
      setSaveAndPayActive(false);
      navigate(`/invoice/${createdInvoiceNumber}`, {
        state: { autoOpenCheckout: true },
      });
    } else if (invoiceCreationPhase === 'failed') {
      setSaveAndPayActive(false);
      setSaveAndPayError(invoiceCreationError ?? 'Failed to create invoice');
    }
  }, [
    saveAndPayActive,
    invoiceCreationPhase,
    createdInvoiceNumber,
    invoiceCreationError,
    navigate,
  ]);

  const handleNewTicket = useCallback(() => {
    resetSubmission();
    setSelectedCustomer(null);
    setSelectedTemplate(null);
    setTemplateVersion(undefined);
    // The previous ticket's checklist must go with its template — left behind,
    // picking any template on the new ticket prompts "change template?".
    detachAllTasks();
    setPendingTemplateChange(null);
    setShowTemplateChangeConfirm(false);
    setPendingAttach(null);
    setAdditionalDetails({});
    setCustomerContextOverrides({});
    setSuppressedTemplateKeys(new Set());
    setLineItems([{
      id: crypto.randomUUID(),
      source: 'manual',
      lineItemId: '',
      category: '',
      quantity: 0,
      unitCost: 0,
      prefixUserBase: '',
      afterHours: false,
      nonChargeable: false,
      lineDetail: '',
      subtotal: 0,
      finalTotal: 0,
      overrideState: createDefaultOverrideState(),
    }]);
    setExpandedItems({});
    setTicketSubject('');
    setTicketDescription('');
    setTicketDetail('');
    setRequestingUser('');
    setShowRequestedByLine(false);
    setLinkedFrom(null);
    markSubjectOverridden(false);
    markDescriptionOverridden(false);
    setTicketStatus('New');
    setTicketDate(new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }));
  }, [resetSubmission, detachAllTasks, markSubjectOverridden, markDescriptionOverridden]);

  // ---------------------------------------------------------------------------
  // Debug panel configuration
  // ---------------------------------------------------------------------------

  const templateLineItems = lineItems.filter((item) => item.source === 'ticketTypeTemplate');
  const manualLineItems = lineItems.filter((item) => item.source === 'manual');

  const formatAge = (cachedAt: number): string => {
    const diffMs = Date.now() - cachedAt;
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  };

  const debugConfig = useMemo<DebugPanelConfig>(() => ({
    sections: [
      { id: 'customer', label: 'Customer' },
      { id: 'ticket', label: 'Ticket' },
      { id: 'cost', label: 'Cost' },
    ],
    hookStates: [
      {
        name: 'useCustomerServices',
        section: 'customer',
        status: customerServicesError ? 'error' : isLoadingCustomerStatuses ? 'loading' : 'loaded',
        error: customerServicesError,
        recordCount: customerStatusServices.length,
        detail: selectedCustomerId ? `customerId: ${selectedCustomerId}` : 'No customer',
      },
      {
        name: 'useTicketTypeTemplates',
        section: 'ticket',
        status: templatesError ? 'error' : isLoadingTemplates ? 'loading' : isTemplatesStale ? 'stale' : 'loaded',
        error: templatesError,
        recordCount: templates.length,
      },
      {
        name: 'usePublicHolidays',
        section: 'cost',
        status: holidaysError ? 'error' : isLoadingHolidays ? 'loading' : 'loaded',
        error: holidaysError,
        recordCount: holidays.length,
      },
      {
        name: 'useProductCatalog',
        section: 'cost',
        status: productsError ? 'error' : isLoadingProducts ? 'loading' : isProductsStale ? 'stale' : 'loaded',
        error: productsError,
        recordCount: products.length,
      },
      {
        name: 'useRenewalSchedule',
        section: 'cost',
        status: renewalScheduleError ? 'error' : isLoadingRenewalSchedule ? 'loading' : 'loaded',
        error: renewalScheduleError,
        recordCount: renewalSchedule.length,
        detail: selectedCustomerId ? `customerId: ${selectedCustomerId}` : 'No customer',
      },
      {
        name: 'usePricingProfilesConfig',
        section: 'cost',
        status: pricingProfilesError ? 'error' : isLoadingPricingProfiles ? 'loading' : isPricingProfilesStale ? 'stale' : 'loaded',
        error: pricingProfilesError,
        detail: pricingProfilesConfig ? `v${pricingProfilesConfig.configVersion}` : undefined,
      },
      {
        name: 'useAfterHoursSuggestion',
        section: 'cost',
        status: 'loaded',
        detail: afterHoursSuggestion.isAfterHours ? `After-hours: ${afterHoursSuggestion.reason}` : 'Business hours',
      },
      {
        name: 'useTaskLists',
        section: 'ticket',
        status: taskListsError ? 'error' : isLoadingTaskLists ? 'loading' : isTaskListsStale ? 'stale' : 'loaded',
        error: taskListsError,
        recordCount: taskListCatalog.taskLists.length,
      },
    ],
    caches: [
      {
        name: 'Product Catalog',
        section: 'cost',
        type: 'memory+localStorage',
        getRecordCount: () => getCacheMetadata()?.recordCount ?? 0,
        getAge: () => {
          const meta = getCacheMetadata();
          return meta ? formatAge(meta.cachedAt) : null;
        },
        getSizeEstimate: () => {
          try {
            const raw = localStorage.getItem('products_cache_v1');
            if (!raw) return '0 KB';
            return `${Math.round(raw.length / 1024)} KB`;
          } catch { return 'unknown'; }
        },
        clear: async () => { await invalidateProductsCache(); await retryProducts(); },
        refresh: async () => { await refreshProductsCache(); },
        getData: () => products,
      },
      {
        name: 'Ticket Type Templates',
        section: 'ticket',
        type: 'memory+localStorage',
        getRecordCount: () => getTemplateCacheMetadata()?.recordCount ?? 0,
        getAge: () => {
          const meta = getTemplateCacheMetadata();
          return meta ? formatAge(meta.cachedAt) : null;
        },
        getSizeEstimate: () => {
          try {
            const raw = localStorage.getItem('ticket_templates_cache_v1');
            if (!raw) return '0 KB';
            return `${Math.round(raw.length / 1024)} KB`;
          } catch { return 'unknown'; }
        },
        clear: async () => { await invalidateTicketTypeTemplatesCache(); await retryTemplates(); },
        refresh: async () => { await refreshTemplates(); },
        getData: () => templates,
      },
      {
        name: 'Task Lists',
        section: 'ticket',
        type: 'memory+localStorage',
        getRecordCount: () => getTaskListsCacheMetadata()?.recordCount ?? 0,
        getAge: () => {
          const meta = getTaskListsCacheMetadata();
          return meta ? formatAge(meta.cachedAt) : null;
        },
        getSizeEstimate: () => {
          try {
            const raw = localStorage.getItem('task_lists_cache_v1');
            if (!raw) return '0 KB';
            return `${Math.round(raw.length / 1024)} KB`;
          } catch { return 'unknown'; }
        },
        clear: async () => { await invalidateTaskLists(); },
        refresh: async () => { await refreshTaskLists(); },
        getData: () => taskListCatalog,
      },
      {
        name: 'Public Holidays',
        section: 'cost',
        type: 'memory',
        getRecordCount: () => holidays.length,
        getData: () => holidays,
        // TODO: No formal cache metadata — see TODO-cache-refresh.md
      },
      {
        name: 'Renewal Schedule',
        section: 'cost',
        type: 'memory',
        getRecordCount: () => renewalSchedule.length,
        getData: () => renewalSchedule,
        // TODO: No formal cache metadata — see TODO-cache-refresh.md
      },
      {
        name: 'Customer Services',
        section: 'customer',
        type: 'memory',
        getRecordCount: () => customerStatusServices.length,
        getData: () => customerStatusServices,
        refresh: () => refreshCustomerServices(),
        // TODO: No formal cache — see TODO-cache-refresh.md
      },
      {
        name: 'Pricing Profiles Config',
        section: 'cost',
        type: 'memory+localStorage',
        getRecordCount: () => {
          if (!pricingProfilesConfig) return 0;
          const defaults = Object.keys(pricingProfilesConfig.defaults ?? {}).length;
          const designations = Object.keys(pricingProfilesConfig.designations ?? {}).length;
          return defaults + designations;
        },
        getAge: () => {
          const meta = getProfilesConfigCacheMetadata();
          return meta ? formatAge(meta.cachedAt) : null;
        },
        getSizeEstimate: () => {
          try {
            const raw = localStorage.getItem('pricing_profiles_cache_v1');
            if (!raw) return '0 KB';
            return `${Math.round(raw.length / 1024)} KB`;
          } catch { return 'unknown'; }
        },
        clear: () => { invalidateProfilesConfigCache(); retryPricingProfiles(); },
        refresh: async () => { await refreshProfilesConfigCache(); await retryPricingProfiles(); },
        getData: () => pricingProfilesConfig,
      },
    ],
    dataSections: [
      // Customer section: customer context
      {
        sectionId: 'customer',
        label: 'Customer Context',
        entries: customerContext && Object.keys(customerContext).length > 0
          ? Object.entries(customerContext).map(([key, value]) => ({
              label: key,
              content: typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''),
            }))
          : [{ label: 'Status', content: 'No customer context' }],
      },
      // Ticket section: selected template
      {
        sectionId: 'ticket',
        label: 'Selected Template',
        entries: selectedTemplate
          ? [
              { label: 'ticketTypeId', content: selectedTemplate.ticketTypeId },
              { label: 'displayName', content: selectedTemplate.displayName },
              { label: 'version', content: String(templateVersion || selectedTemplate.templateVersion) },
              { label: 'group', content: selectedTemplate.group },
              { label: 'serviceKey', content: selectedTemplate.serviceKey },
              { label: 'billingMode', content: selectedTemplate.billingMode },
              { label: 'billingStrategy', content: selectedTemplate.billingStrategy },
              { label: 'subjectTemplate', content: selectedTemplate.subjectTemplate ?? '—' },
              { label: 'descriptionTemplate', content: selectedTemplate.descriptionTemplate ?? '—' },
              { label: 'additionalFields', content: `${selectedTemplate.additionalFields?.length ?? 0} field(s)` },
              { label: 'templateLineItems', content: `${selectedTemplate.templateLineItems?.length ?? 0} definition(s)` },
            ]
          : [{ label: 'Status', content: 'No template selected' }],
      },
      // Ticket section: template line item definitions (from template config)
      ...(selectedTemplate?.templateLineItems && selectedTemplate.templateLineItems.length > 0
        ? [{
            sectionId: 'ticket',
            label: `Template Line Item Definitions (${selectedTemplate.templateLineItems.length})`,
            entries: [{
              label: 'definitions',
              content: `${selectedTemplate.templateLineItems.length} definitions`,
              table: {
                headers: ['Template Key', 'Line Item ID', 'Category', 'Default Qty', 'Default Cost', 'Pricing Rule'],
                rows: selectedTemplate.templateLineItems.map((tli) => [
                  tli.templateKey,
                  tli.lineItemId,
                  tli.category,
                  String(tli.defaultQuantity),
                  tli.defaultUnitCost !== undefined ? `$${tli.defaultUnitCost.toFixed(2)}` : '—',
                  tli.pricingRule ?? '—',
                ]),
              },
            }],
          }]
        : []),
      // Ticket section: additional field definitions (from template config)
      ...(selectedTemplate?.additionalFields && selectedTemplate.additionalFields.length > 0
        ? [{
            sectionId: 'ticket',
            label: `Additional Field Definitions (${selectedTemplate.additionalFields.length})`,
            entries: [{
              label: 'fields',
              content: `${selectedTemplate.additionalFields.length} fields`,
              table: {
                headers: ['Field Key', 'Label', 'Type', 'Required', 'Options'],
                rows: selectedTemplate.additionalFields.map((f) => [
                  f.fieldKey,
                  f.label,
                  f.type,
                  f.required,
                  f.options ? `${f.options.length} option(s)` : '—',
                ]),
              },
            }],
          }]
        : []),
      // Ticket section: additional details
      {
        sectionId: 'ticket',
        label: `Additional Details (${Object.keys(additionalDetails).length})`,
        entries: Object.keys(additionalDetails).length > 0
          ? Object.entries(additionalDetails).map(([key, value]) => ({
              label: key,
              content: String(value),
            }))
          : [{ label: 'Status', content: 'No variables set' }],
      },
      // Cost section: template line items
      {
        sectionId: 'cost',
        label: `Template Line Items (${templateLineItems.length})`,
        entries: templateLineItems.length > 0
          ? [{
              label: 'items',
              content: `${templateLineItems.length} items`,
              table: {
                headers: ['Template Key', 'Category', 'Qty', 'Cost', 'Overrides', 'Pricing'],
                rows: templateLineItems.map((item) => {
                  const overrideCount = item.overrideState
                    ? Object.values(item.overrideState).filter(Boolean).length
                    : 0;
                  return [
                    item.templateKey || '—',
                    item.category,
                    String(item.quantity),
                    `$${item.unitCost.toFixed(2)}`,
                    overrideCount > 0 ? `${overrideCount} field(s)` : 'None',
                    item.pricingExplanation || '—',
                  ];
                }),
              },
            }]
          : [{ label: 'Status', content: 'No template line items' }],
      },
      // Cost section: suppressed template keys
      ...(suppressedTemplateKeys.size > 0
        ? [{
            sectionId: 'cost',
            label: `Suppressed Template Keys (${suppressedTemplateKeys.size})`,
            entries: Array.from(suppressedTemplateKeys).map((key) => ({
              label: key,
              content: 'suppressed',
            })),
          }]
        : []),
      // Cost section: manual line items
      ...(manualLineItems.length > 0
        ? [{
            sectionId: 'cost',
            label: `Manual Line Items (${manualLineItems.length})`,
            entries: [{
              label: 'items',
              content: `${manualLineItems.length} items`,
              table: {
                headers: ['Category', 'Prefix', 'Qty', 'Cost'],
                rows: manualLineItems.map((item) => [
                  item.category,
                  item.prefixUserBase,
                  String(item.quantity),
                  `$${item.unitCost.toFixed(2)}`,
                ]),
              },
            }],
          }]
        : []),
      // Cost section: resolved pricing profile
      {
        sectionId: 'cost',
        label: 'Resolved Pricing Profile',
        entries: (() => {
          const resolved = customerContext.resolvedPricingProfile;
          if (!resolved || Object.keys(resolved).length === 0) {
            return [{ label: 'Status', content: selectedCustomer ? 'No resolved profile' : 'No customer selected' }];
          }
          const slots = Object.entries(resolved);
          return [{
            label: 'slots',
            content: `${slots.length} delivery mode(s)`,
            table: {
              headers: ['Delivery Mode', 'SKU', 'Unit Cost', 'AH SKU', 'AH Cost', 'Default Qty'],
              rows: slots.map(([mode, slot]) => [
                mode,
                slot.sku,
                `$${slot.unitCost.toFixed(2)}`,
                slot.afterHoursSku ?? '—',
                slot.afterHoursUnitCost !== undefined ? `$${slot.afterHoursUnitCost.toFixed(2)}` : '—',
                Object.entries(slot.defaultQuantity).map(([w, q]) => `${w}: ${q}`).join(', ') || '—',
              ]),
            },
          }];
        })(),
      },
      // Cost section: pricing profiles config summary
      {
        sectionId: 'cost',
        label: 'Pricing Profiles Config',
        entries: pricingProfilesConfig
          ? [
              { label: 'configVersion', content: pricingProfilesConfig.configVersion },
              { label: 'defaults', content: `${Object.keys(pricingProfilesConfig.defaults ?? {}).length} customer type(s)` },
              { label: 'designations', content: `${Object.keys(pricingProfilesConfig.designations ?? {}).length} designation(s)` },
            ]
          : [{ label: 'Status', content: pricingProfilesError ?? 'Loading...' }],
      },
    ],
  }), [
    customerServicesError, isLoadingCustomerStatuses, customerStatusServices,
    selectedCustomerId, isLoadingTemplates, isTemplatesStale, templatesError,
    templates, holidaysError, isLoadingHolidays, holidays, productsError,
    isLoadingProducts, isProductsStale, products, renewalScheduleError,
    isLoadingRenewalSchedule, renewalSchedule, afterHoursSuggestion,
    isLoadingPricingProfiles, isPricingProfilesStale, pricingProfilesError,
    pricingProfilesConfig, selectedCustomer,
    customerContext, selectedTemplate, templateVersion, additionalDetails,
    templateLineItems, manualLineItems, suppressedTemplateKeys,
    refreshCustomerServices, retryProducts, retryTemplates, refreshTemplates, retryPricingProfiles,
    taskListsError, isLoadingTaskLists, isTaskListsStale, taskListCatalog,
    refreshTaskLists, invalidateTaskLists,
  ]);

  return (
    <PageContainer
      className={`ticket-entry ${FEATURE_TICKET_LAYOUT_V2 ? 'ticket-entry--v2' : ''}`}
      sx={{ background: '#f0f4f9', minHeight: '100%' }}
    >
      {/* §5: omit the page heading for New Ticket (the nav tab names it). Keep
          the "Edit Ticket #N" heading in edit mode for context. No back arrow —
          Back always returns to the Work Item Dashboard, which is in the nav. */}
      {isEditMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a' }}>
            {`Edit Ticket${editWorkItem?.number ? ` #${editWorkItem.number}` : ''}`}
          </Typography>
        </Box>
      )}
      <Stack spacing={4}>
        <Box sx={{ position: 'relative' }}>
          {isSubmitting && (
            <Backdrop
              open
              sx={{
                position: 'absolute',
                zIndex: 10,
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                borderRadius: 2,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={48} sx={{ color: '#4A90D9' }} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  {isEditMode ? 'Updating ticket...' : 'Submitting ticket...'}
                </Typography>
              </Box>
            </Backdrop>
          )}
          <Box className="ticket-entry__grid">
          <Box className="ticket-entry__customer" sx={[CARD_SX, { p: { xs: 2.5, md: 3.5 }, minWidth: 0, overflowX: 'hidden' }]} {...sectionProps('customer')}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 36, justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
              {selectedCustomer ? (
                <CustomerHeader
                  name={selectedCustomer.displayName}
                  dataverseId={selectedCustomer.dataverseCustomerId}
                  repairShoprId={selectedCustomerRepairShoprId}
                  customerType={selectedCustomer.customerType}
                  designation={selectedCustomer.designation}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: '#f0f4f9',
                      color: '#666666',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      flexShrink: 0,
                    }}
                    aria-hidden
                  >
                    —
                  </Avatar>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
                    Customer
                  </Typography>
                </Box>
              )}
            </Box>
              <Stack spacing={2}>
              <CustomerLookupControl
                value={selectedCustomer}
                onChange={setSelectedCustomer}
                onTabAway={() => ticketTypePickerRef.current?.focus()}
                onAddNew={() => setNewCustomerOpen(true)}
                autoFocus={!isEditMode}
                disabled={formDisabled || isEditMode}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <FormField label="Phone">
                    <div className="ticket-entry__field-icon">
                      <IconPhone size={15} stroke={1.5} />
                      <input
                        value={formatAuLandline(selectedCustomer?.phone)}
                        placeholder="02-3456-7890"
                        readOnly
                      />
                    </div>
                  </FormField>
                </div>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <FormField label="Mobile">
                    <div className="ticket-entry__field-icon">
                      <IconDeviceMobile size={15} stroke={1.5} />
                      <input
                        value={formatAuMobile(selectedCustomer?.mobile)}
                        placeholder="0412-345-678"
                        readOnly
                      />
                    </div>
                  </FormField>
                </div>
              </Box>
              <FormField label="Email">
                <div className="ticket-entry__field-icon">
                  <IconMail size={15} stroke={1.5} />
                  <input
                    value={selectedCustomer?.email || ''}
                    placeholder="name@example.com"
                    readOnly
                  />
                </div>
              </FormField>
              <Box sx={{ minWidth: 0 }}>
                {/* Static section-header band — mirrors the CollapsibleSection header
                    (Services/Notes/Aliases) but is a label, not a toggle. */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    p: '10px 16px',
                    borderTop: '0.5px solid #e2eaf3',
                    bgcolor: '#f0f3f7',
                    userSelect: 'none',
                  }}
                >
                  <IconLink size={18} stroke={1.5} style={{ color: '#aaaaaa', flexShrink: 0 }} />
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: '#646464', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
                    Shortcuts
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', p: '10px 16px', borderTop: '0.5px solid #eef2f7', bgcolor: '#ffffff' }}>
                  <Box
                    component="button"
                    type="button"
                    title="Open RepairShopr Customer Record"
                    disabled={!selectedCustomerRepairShoprId}
                    onClick={() => window.open(`https://techsolvers.repairshopr.com/customers/${selectedCustomerRepairShoprId}`, '_blank', 'noopener,noreferrer')}
                    sx={SHORTCUT_TILE_SX}
                  >
                    <IconUser size={18} stroke={1.5} />
                    <Box component="span" sx={SHORTCUT_LABEL_SX}>Customer</Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    title={selectedCustomer?.domains ? selectedCustomer.domains.split(/[,;]\s*/).join(', ') : 'No domains'}
                    disabled={!selectedCustomer}
                    sx={SHORTCUT_TILE_SX}
                  >
                    <IconWorld size={18} stroke={1.5} />
                    <Box component="span" sx={SHORTCUT_LABEL_SX}>Domains</Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    title="Open Invoices"
                    disabled={!selectedCustomerRepairShoprId}
                    onClick={() => window.open(`https://techsolvers.repairshopr.com/invoices?search%5Bcustomer_id%5D=${selectedCustomerRepairShoprId}`, '_blank', 'noopener,noreferrer')}
                    sx={SHORTCUT_TILE_SX}
                  >
                    <IconFileInvoice size={18} stroke={1.5} />
                    <Box component="span" sx={SHORTCUT_LABEL_SX}>Invoices</Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    title="Open Tickets"
                    disabled={!selectedCustomerRepairShoprId}
                    onClick={() => window.open(`https://techsolvers.repairshopr.com/tickets?commit=Search&ticket_search%5Bquery%5D%5Ball_fields%5D=customer_id%3A${selectedCustomerRepairShoprId}&ticket_search_id=all`, '_blank', 'noopener,noreferrer')}
                    sx={SHORTCUT_TILE_SX}
                  >
                    <IconTicket size={18} stroke={1.5} />
                    <Box component="span" sx={SHORTCUT_LABEL_SX}>Tickets</Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    title={
                      monthlySchedule.kind === 'paused'
                        ? 'Monthly schedule paused — open to review'
                        : monthlySchedule.kind === 'none'
                          ? 'No monthly schedule — open customer to set one up'
                          : 'Open Monthly Schedule'
                    }
                    disabled={!selectedCustomerRepairShoprId}
                    onClick={() => openSchedule(monthlySchedule)}
                    sx={SHORTCUT_TILE_SX}
                  >
                    <IconCalendar size={18} stroke={1.5} style={monthlySchedule.kind === 'active' ? undefined : { color: '#bbbbbb' }} />
                    <Box component="span" sx={SHORTCUT_LABEL_SX}>Monthly</Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    title={
                      annualSchedule.kind === 'paused'
                        ? 'Annual schedule paused — open to review'
                        : annualSchedule.kind === 'none'
                          ? 'No annual schedule — open customer to set one up'
                          : 'Open Annual Schedule'
                    }
                    disabled={!selectedCustomerRepairShoprId}
                    onClick={() => openSchedule(annualSchedule)}
                    sx={SHORTCUT_TILE_SX}
                  >
                    <IconCalendarStats size={18} stroke={1.5} style={annualSchedule.kind === 'active' ? undefined : { color: '#bbbbbb' }} />
                    <Box component="span" sx={SHORTCUT_LABEL_SX}>Annual</Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <CustomerServicesPanel
                  ref={servicesPanelRef}
                  dataverseCustomerId={selectedCustomerId}
                  services={customerStatusServices}
                  isLoading={isLoadingCustomerStatuses}
                  error={customerServicesError}
                  canEditServices={canEditServices}
                  refresh={refreshCustomerServices}
                  create={createCustomerService}
                  update={updateCustomerService}
                  focusServiceId={focusedServiceId}
                  focusRequestKey={serviceFocusRequestKey}
                  onManageEset={() => setEsetPanelOpen(true)}
                  customerName={selectedCustomer?.displayName}
                />
              </Box>
              {/* Always render so the section headers/placeholders show before a
                  customer is selected (matching the Services panel). Edit/add
                  affordances are gated on a customer being selected. */}
              <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <CustomerNotesPanel
                  notes={customerNotes}
                  isLoading={isLoadingCustomerNotes}
                  error={customerNotesError}
                  canEditNotes={canEditServices && Boolean(selectedCustomerId)}
                  refresh={refreshCustomerNotes}
                  update={updateCustomerNotes}
                />
              </Box>
              <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <CustomerAliasesPanel
                  aliases={customerAliases}
                  vendors={vendors}
                  isLoading={isLoadingCustomerAliases}
                  error={customerAliasesError}
                  canEditMatchingAliases={canEditServices && Boolean(selectedCustomerId)}
                  canDeleteAliases={canEditServices && Boolean(selectedCustomerId)}
                  canToggleActive={canEditServices && Boolean(selectedCustomerId)}
                  refresh={refreshCustomerAliases}
                  create={createCustomerAlias}
                  update={updateCustomerAlias}
                  remove={removeCustomerAlias}
                />
              </Box>
            </Stack>
          </Box>

          <Box className="ticket-entry__ticket" sx={[CARD_SX, { p: { xs: 2.5, md: 3.5 }, height: '100%' }]} {...sectionProps('ticket')}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 36, justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
                  {rsTicketNumber ? `Ticket ${rsTicketNumber}` : 'Ticket'}
                </Typography>
                {rsTicketId ? (
                  <Chip
                    tone="info"
                    onClick={() => {
                      window.open(
                        `https://techsolvers.repairshopr.com/tickets/${rsTicketId}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                  >
                    <IconExternalLink size={14} stroke={1.5} /> RS
                  </Chip>
                ) : isAccepted ? (
                  <Chip tone="info"><CircularProgress size={12} /> RS</Chip>
                ) : null}
                {dvJobId ? (
                  <Chip tone="info" title={`Dataverse ID: ${dvJobId}`}>
                    <IconDatabase size={14} stroke={1.5} /> DV
                  </Chip>
                ) : isAccepted ? (
                  <Chip tone="info"><CircularProgress size={12} /> DV</Chip>
                ) : null}
                <LinkedTicketChips
                  parent={isEditMode
                    ? (editWorkItem?.linkedFromJobNumber
                      ? { number: editWorkItem.linkedFromJobNumber, title: editWorkItem.linkedFromJobTitle }
                      : null)
                    : linkedFrom}
                  children={linkedChildren}
                  isLoading={isLoadingLinkedChildren}
                />
              </Box>
              {(printableRsTicketId || (isEditMode && sourceJobGuid)) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {isEditMode && sourceJobGuid && onCreateLinked && (
                    <Tooltip
                      arrow
                      title={`Create a new ticket from a ticket template${
                        selectedCustomer ? ` for ${selectedCustomer.displayName}` : ''
                      }, linked to ${editWorkItem?.number ? `#${editWorkItem.number}` : 'this ticket'}`}
                    >
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<IconLink size={16} stroke={1.5} />}
                          onClick={() => setLinkDialogOpen(true)}
                          disabled={isSubmitting || isSavingLineItems}
                        >
                          Link
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {printableRsTicketId && (
                    <>
                      <Tooltip
                        arrow
                        title={`Print the job label for this ticket on the ${labelLaneName(labelRole)} label printer${
                          labelRole ? ' (your default — change it in Settings)' : ''
                        }`}
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={printingDocument === 'label'
                              ? <CircularProgress size={14} color="inherit" />
                              : <IconPrinter size={16} stroke={1.5} />}
                            onClick={() => handlePrintDocument('label')}
                            disabled={printingDocument !== null}
                            aria-label="Print Label"
                          >
                            Label
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip arrow title="Print the A4 job ticket sheet for this ticket on the office printer">
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={printingDocument === 'sheet'
                              ? <CircularProgress size={14} color="inherit" />
                              : <IconPrinter size={16} stroke={1.5} />}
                            onClick={() => handlePrintDocument('sheet')}
                            disabled={printingDocument !== null}
                            aria-label="Print A4 Ticket"
                          >
                            Ticket
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Box>
              )}
            </Box>
            <Stack spacing={2}>
              <GroupedTicketTypePicker
                ref={ticketTypePickerRef}
                value={selectedTemplate}
                onChange={handleTemplateChange}
                templates={visibleTemplates}
                isLoading={isLoadingTemplates}
                outOfServiceWarning={outOfServiceWarning}
                onTabAway={() => {
                  const firstInput = additionalDetailsContainerRef.current?.querySelector<HTMLInputElement>('input, textarea');
                  (firstInput || subjectRef.current)?.focus();
                }}
                disabled={formDisabled}
              />
              {eolp2Billing?.blocked && (
                <Alert severity="warning" sx={{ mt: -1 }}>
                  This customer has no M365 subscription on an invoice schedule, so
                  the Exchange Online Plan 2 charge can't be added. Set up their M365
                  subscription schedule first — the ticket will still save the labour.
                </Alert>
              )}
              {/* New tickets pick status via the Save as New / Save as Resolved
                  buttons at the bottom; edit mode keeps the full Status select. */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 0', minWidth: 200 }}>
                  <div style={{ width: 200 }}>
                    <DateField
                      label="Date"
                      value={ticketDate || null}
                      onChange={(iso) => setTicketDate(iso ?? '')}
                      disabled={formDisabled}
                    />
                  </div>
                </Box>
                <Box sx={{ flex: '1 1 0', minWidth: 200, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {isEditMode && (
                    <div style={{ width: 200 }}>
                      <SmartSelect
                        label="Status"
                        value={ticketStatus}
                        options={TICKET_STATUS_OPTIONS}
                        onChange={(v) => v && setTicketStatus(v)}
                        disabled={formDisabled}
                      />
                    </div>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showAllTemplates}
                        onChange={(e) => setShowAllTemplates(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        Show all templates
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Box>
              {hasBlockingTasks && (isEditMode ? ticketStatus.toLowerCase() === 'resolved' : true) && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  All tasks must be actioned before resolving
                </Alert>
              )}
              <TaskListPicker
                availableLists={taskListCatalog.taskLists}
                isLoading={isLoadingTaskLists}
                onAttach={handleAttachTaskList}
                disabled={formDisabled}
              />
              <div ref={additionalDetailsContainerRef}>
                {isBusinessCustomer && (
                  <div style={{ marginBottom: 16 }}>
                    <FormField
                      label="Requesting User"
                      disabled={formDisabled}
                      error={!isEditMode && selectedTemplate && !requestingUser.trim() ? 'Requesting user is required' : undefined}
                    >
                      <input
                        value={requestingUser}
                        placeholder="Name of the person requesting this work"
                        onChange={(e) => handleRequestingUserChange(e.target.value)}
                        disabled={formDisabled}
                      />
                    </FormField>
                  </div>
                )}
                {selectedTemplate && selectedTemplate.additionalFields && (
                  <AdditionalDetailsRenderer
                    fields={selectedTemplate.additionalFields}
                    values={additionalDetails}
                    onChange={handleAdditionalDetailsChange}
                    customerContext={customerContext}
                    errors={fieldRuleErrors}
                  />
                )}
                {checklist && checklist.sections.length > 0 && checklistProgress && (
                  <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '10px 16px', mt: 2, mb: 2, borderTop: '0.5px solid #e2eaf3', bgcolor: '#f0f3f7' }}>
                    <IconChecklist size={18} stroke={1.5} style={{ color: '#aaaaaa', flexShrink: 0 }} />
                    <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: '#646464', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      Checklist
                    </Box>
                  </Box>
                  <ChecklistPanel
                    checklist={checklist}
                    progress={checklistProgress}
                    taskDropdowns={dropdownCatalog.dropdowns}
                    onSetStatus={setTaskStatus}
                    onSetNote={setTaskNote}
                    onSetOption={setTaskOption}
                    onRemoveTask={removeTask}
                    onRestoreTask={restoreTask}
                    onRemoveSection={removeSection}
                    onRestoreSection={restoreSection}
                    onAddManualTask={addManualTask}
                    disabled={formDisabled}
                  />
                  </>
                )}
              </div>
              <FormField
                label="Subject"
                disabled={formDisabled}
                error={(selectedTemplate || isEditMode) && !ticketSubject.trim() ? 'Subject is required' : undefined}
              >
                <input
                  ref={subjectRef}
                  value={ticketSubject}
                  placeholder="Ticket subject"
                  onChange={(e) => {
                    setTicketSubject(e.target.value);
                    markSubjectOverridden(true);
                  }}
                  disabled={formDisabled}
                />
              </FormField>
              {/* UI label "Detail" maps to internal `ticketDescription` /
                  `request.description` / template var `{ticketDescription}`. */}
              <FormField label="Detail" disabled={formDisabled}>
                <textarea
                  ref={descriptionRef}
                  rows={3}
                  value={composedDescription}
                  placeholder="Detail of work performed — appears on the labour line of the customer's invoice."
                  disabled={formDisabled}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (showRequestedByLine) {
                      const firstNewlineIdx = newValue.indexOf('\n');
                      setTicketDescription(firstNewlineIdx >= 0 ? newValue.substring(firstNewlineIdx + 1) : '');
                    } else {
                      setTicketDescription(newValue);
                    }
                    markDescriptionOverridden(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !e.shiftKey) {
                      e.preventDefault();
                      saveButtonRef.current?.focus();
                    }
                  }}
                />
              </FormField>
              {/* UI label "Comment" maps to internal `ticketDetail` /
                  `request.detail`, posted as a hidden comment on the RS ticket
                  on creation. Distinct from `workItem.notes`. */}
              <FormField label="Comment" disabled={formDisabled}>
                <textarea
                  rows={4}
                  value={ticketDetail}
                  placeholder="Internal comment — posted as a hidden comment on the RS ticket. Not visible to the customer."
                  disabled={formDisabled}
                  onChange={(e) => setTicketDetail(e.target.value)}
                />
              </FormField>
            </Stack>
          </Box>

          <Box
            className="ticket-entry__cost"
            sx={[
              CARD_SX,
              {
                p: { xs: 3, md: 4 },
                height: '100%',
              },
            ]}
            {...sectionProps('cost')}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2.5,
                flexWrap: 'wrap',
                gap: 1,
                ...(afterHoursSuggestion.isAfterHours && {
                  borderRadius: 1,
                }),
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
                Cost
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {afterHoursSuggestion.isAfterHours && (
                  <Chip tone="warning" onClick={(e) => { e.stopPropagation(); navigateToSection('cost'); }}>
                    {afterHoursSuggestion.reason}
                  </Chip>
                )}
              </Box>
            </Box>
            <Stack spacing={2}>
              {(() => {
                const visibleLineItems = lineItems.filter(item => item.source !== 'ticketTypeTemplate' || item.quantity > 0 || item.overrideState?.quantity);
                return (
                  // Subtly-tinted band so the white line-item cards read as nested.
                  <Box sx={{ bgcolor: '#f7f9fc', p: 1, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {visibleLineItems.map((lineItem, index) => (
                      <LineItemCard
                        key={lineItem.id}
                        variant="v2"
                        index={index}
                        lineItem={lineItem}
                        expanded={expandedItems[lineItem.id] || false}
                        canDelete={lineItems.length > 1}
                        onExpandChange={handleExpandChange}
                        onUpdate={handleUpdateLineItem}
                        onDelete={handleDeleteLineItem}
                        products={products}
                        resolvedPricingProfile={customerContext.resolvedPricingProfile}
                      />
                    ))}
                    <Box
                      component="button"
                      type="button"
                      onClick={handleAddLineItem}
                      disabled={formDisabled}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        alignSelf: 'flex-start',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        p: '6px 4px',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#4A90D9',
                        '&:hover:not(:disabled)': { color: '#3B82C4' },
                        '&:disabled': { color: '#bbbbbb', cursor: 'default' },
                      }}
                    >
                      <IconPlus size={14} stroke={1.5} />
                      Add line item
                    </Box>
                  </Box>
                );
              })()}
              <Box
                sx={{
                  mt: 4,
                  pt: 2.5,
                  borderTop: '1px solid',
                  borderColor: '#e2eaf3',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: { xs: 2.5, md: 3.5 },
                    py: { xs: 2.5, md: 2.75 },
                    bgcolor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: '#f0f4f9',
                    borderRadius: 1.5,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#888888',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Ticket Total
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: 700,
                      color: '#1a1a1a',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.1,
                      textAlign: 'right',
                    }}
                  >
                    ${ticketTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              {!isEditMode && (
                <FollowUpTodosSection
                  followUps={followUpResolution.followUps}
                  problems={followUpResolution.problems}
                  disabled={formDisabled}
                  onNoChargeChange={handleFollowUpNoCharge}
                />
              )}
              <div className="ticket-entry__save">
              {isEditMode ? (
                (() => {
                  const label = isSubmitting
                    ? 'Submitting...'
                    : isOptimisticSuccess
                      ? 'Ticket Updated'
                      : 'Update Ticket';
                  return (
                    <Tooltip title={saveDisabledReason} disableHoverListener={canSave}>
                      <Box component="span" sx={{ width: '100%', display: 'block', cursor: canSave ? 'pointer' : 'not-allowed' }}>
                        <Button
                          ref={saveButtonRef}
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={!canSave || saveAndPayActive}
                          onClick={() => handleSaveTicket()}
                          sx={[SAVE_BTN_SX, { mt: 2 }]}
                        >
                          {label}
                        </Button>
                      </Box>
                    </Tooltip>
                  );
                })()
              ) : (
                (() => {
                  const newDisabled = !baseCanSave || saveAndPayActive;
                  const resolvedDisabled = newDisabled || hasBlockingTasks;
                  const inProgress = saveAndPayActive
                    ? isSubmitting
                      ? 'Saving ticket...'
                      : invoiceCreationPhase === 'submitting' || invoiceCreationPhase === 'accepted'
                        ? 'Creating invoice...'
                        : 'Working...'
                    : isSubmitting
                      ? 'Submitting...'
                      : null;
                  return (
                    <Box sx={{ mt: 2 }}>
                      {isOptimisticSuccess ? (
                        <Button variant="contained" size="large" fullWidth disabled sx={SAVE_BTN_SX}>
                          Ticket Submitted
                        </Button>
                      ) : inProgress ? (
                        <Button variant="contained" size="large" fullWidth disabled sx={SAVE_BTN_SX}>
                          {inProgress}
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={saveDisabledReason} disableHoverListener={!newDisabled}>
                            <Box component="span" sx={{ flex: 1, display: 'block', cursor: newDisabled ? 'not-allowed' : 'pointer' }}>
                              <Button
                                ref={saveButtonRef}
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={newDisabled}
                                onClick={() => handleSaveAs('New')}
                                sx={SAVE_BTN_SX}
                              >
                                Save as New
                              </Button>
                            </Box>
                          </Tooltip>
                          <Tooltip title={hasBlockingTasks ? 'All tasks must be actioned before resolving' : saveDisabledReason} disableHoverListener={!resolvedDisabled}>
                            <Box component="span" sx={{ flex: 1, display: 'block', cursor: resolvedDisabled ? 'not-allowed' : 'pointer' }}>
                              <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={resolvedDisabled}
                                onClick={() => handleSaveAs('Resolved')}
                                sx={SAVE_BTN_SX}
                              >
                                Save as Resolved
                              </Button>
                            </Box>
                          </Tooltip>
                        </Box>
                      )}
                      {hasMonthlyInvoiceSchedule === true && !isOptimisticSuccess && !inProgress && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}
                        >
                          Existing business customer — to be billed on next monthly invoice
                        </Typography>
                      )}
                    </Box>
                  );
                })()
              )}
              </div>
              {saveAndPayError && (
                <Alert
                  severity="error"
                  sx={{ mt: 1.5 }}
                  onClose={() => setSaveAndPayError(null)}
                >
                  {saveAndPayError}
                </Alert>
              )}
              {lineItemSaveError && (
                <Alert
                  severity="error"
                  sx={{ mt: 1.5 }}
                  onClose={() => setLineItemSaveError(null)}
                >
                  Line items could not be saved, so the ticket was not updated: {lineItemSaveError}
                </Alert>
              )}
              {followUpResults.filter(r => r.error).map((r, i) => (
                <Alert key={`follow-up-error-${i}`} severity="warning" sx={{ mt: 1.5 }}>
                  The ticket was raised, but its follow-up "{r.title}" ({r.scheduledDate}) could not be scheduled: {r.error}. Add it as a to-do by hand.
                </Alert>
              ))}
              {followUpResults.some(r => r.id) && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Follow-up scheduled: {followUpResults.filter(r => r.id).map(r => `${r.title} (${r.scheduledDate})`).join(', ')}
                </Alert>
              )}
              {activeError && !backgroundFailed && (
                <Alert
                  severity="error"
                  sx={{ mt: 1.5 }}
                  action={
                    <Button color="inherit" size="small" onClick={activeReset}>
                      Retry
                    </Button>
                  }
                >
                  {activeError}
                </Alert>
              )}
              {backgroundFailed && (
                <Alert
                  severity="error"
                  sx={{ mt: 1.5 }}
                  action={
                    <Button color="inherit" size="small" onClick={activeReset}>
                      Retry
                    </Button>
                  }
                >
                  {isEditMode ? 'Ticket update failed' : 'Ticket submission was accepted but creation failed'}: {activeError}
                </Alert>
              )}
              {isOptimisticSuccess && !backgroundFailed && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  {isEditMode
                    ? (isAccepted ? 'Update submitted \u2014 updating in RepairShopr and Dataverse...' : 'Ticket updated successfully')
                    : (isAccepted
                      ? 'Ticket submitted \u2014 creating in RepairShopr and Dataverse...'
                      : `Ticket ${rsTicketNumber ? `#${rsTicketNumber}` : ''} created successfully`)}
                </Alert>
              )}
              {(isOptimisticSuccess || backgroundFailed) && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={isEditMode ? onBack : handleNewTicket}
                  sx={{
                    mt: 1.5,
                    py: 1.25,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: '#4A90D9',
                    color: '#4A90D9',
                    '&:hover': { borderColor: '#3B82C4', bgcolor: '#EDF5FF' },
                  }}
                >
                  {isEditMode ? 'Back to Dashboard' : 'New Ticket'}
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
        </Box>

        <PastTicketsTable customerId={selectedCustomerId} />

        <DebugPanel config={debugConfig} focusedSection={focusedSection} navigationRequest={navigationRequest} />

        <LinkedTicketDialog
          open={linkDialogOpen}
          sourceNumber={editWorkItem?.number}
          customerName={selectedCustomer?.displayName}
          templates={visibleTemplates}
          isLoadingTemplates={isLoadingTemplates}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={pendingLinkTemplate !== null}
          saveBlockedReason={canSave ? undefined : saveDisabledReason}
          onCancel={() => { setLinkDialogOpen(false); setPendingLinkTemplate(null); }}
          onContinue={handleLinkContinue}
        />

        <TemplateChangeConfirmDialog
          open={showTemplateChangeConfirm}
          onConfirm={handleConfirmTemplateChange}
          onCancel={handleCancelTemplateChange}
        />

        <TaskListContextPromptDialog
          open={!!pendingAttach}
          taskListLabel={pendingAttach?.label ?? ''}
          missingFieldValueKeys={pendingAttach?.missing.fieldValueKeys ?? []}
          missingCustomerContextKeys={pendingAttach?.missing.customerContextKeys ?? []}
          onCancel={() => setPendingAttach(null)}
          onSubmit={handleContextPromptSubmit}
        />

        <QuickNewCustomerDialog
          open={newCustomerOpen}
          onClose={() => setNewCustomerOpen(false)}
          onCreated={handleCustomerCreated}
        />
      </Stack>
      <Snackbar
        open={!!printSnack}
        autoHideDuration={4000}
        onClose={() => setPrintSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {printSnack ? (
          <Alert
            severity={printSnack.severity}
            onClose={() => setPrintSnack(null)}
            sx={{ width: '100%' }}
          >
            {printSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
      <Snackbar
        open={!!scheduleSnack}
        autoHideDuration={6000}
        onClose={() => setScheduleSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {scheduleSnack ? (
          <Alert
            severity={scheduleSnack.severity}
            onClose={() => setScheduleSnack(null)}
            sx={{ width: '100%' }}
          >
            {scheduleSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
      <EsetManagePanel
        open={esetPanelOpen}
        onClose={() => setEsetPanelOpen(false)}
        customerGuid={selectedCustomerId}
        customerId={selectedCustomer?.repairShoprCustomerId ?? null}
        customerName={selectedCustomer?.displayName ?? null}
      />
    </PageContainer>
  );
};
